import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { 
  Plus, Search, Filter, Download, Receipt, 
  Wallet, CreditCard, DollarSign, Calendar,
  Edit2, Trash2, X, Check, ImagePlus, Building2, Eye, FileImage, FileText,
  List, CalendarDays, ChevronLeft, ChevronRight, ChevronDown, Clipboard, History, ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../api/axios';
import { formatApiError } from '../utils/apiErrors';
import { doesStaffHavePayrollActivityInRange, isStaffActiveOnDate, buildStaffDropdownOptions, mergeStaffWithLifecycleHistory, roundSalaryDisplayAmount } from '../utils/payroll';
import { alignSalarySummaryRowsWithEvents, buildSalaryCalendarData, buildSalaryGrandTotal, buildSalaryOverviewParams, buildSalaryPaymentStaffOptions, buildSalaryStaffOptions, filterSalarySummaryRows, formatSalaryEventDue, formatSignedCarry, getSalaryCycleMonthReference } from '../utils/salaryOverview';
import UiDateRangeBlock from '../components/ui/UiDateRangeBlock';
import UiDateField from '../components/ui/UiDateField';
import UiTimeField from '../components/ui/UiTimeField';
import { buildStandardPresetOptions, getBusinessBoundaryTimes, resolveStandardStartEdit, shiftDateRangeByPreset, shouldKeepStandardPresetForEnd } from '../utils/dateRangeUi';
import { formatUiCurrencyCompact, formatUiCurrencyDetailed, formatUiDate, formatUiDateTimeRange, formatUiMonthYear } from '../utils/uiFormat';
import { fetchPaginatedCollection } from '../utils/apiPagination';
import { UiSummaryCard, UiSummaryCards } from '../components/ui/UiSummaryCard';
import { UiTabs, UiViewToggle } from '../components/ui/UiTabs';
import SalaryComputationInfo from '../components/SalaryComputationInfo';
import SalaryCycleSummary from '../components/SalaryCycleSummary';
import { LifecycleBadge, EventLegend, offDayFilled } from '../components/LifecycleBadge';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: <Wallet className="w-4 h-4" /> },
  { value: 'digital', label: 'Digital/Bank', icon: <CreditCard className="w-4 h-4" /> },
  { value: 'card', label: 'Card', icon: <CreditCard className="w-4 h-4" /> },
];

const EXPENSE_TABS = [
  { id: 'all', label: 'All', categories: null },
  { id: 'purchases', label: 'Inventory', categories: ['raw_material'] },
  { id: 'salary', label: 'Salary', categories: ['salary'] },
  { id: 'bills', label: 'Bills', categories: ['bills'] },
  { id: 'maintenance', label: 'Maintenance', categories: ['maintenance'] },
];

const GLOBAL_SEARCH_PAGE_SIZE = 100;
const EMPTY_GLOBAL_SEARCH_RESULTS = { purchases: [], maintenance: [], bills: [], salary: [] };
const EMPTY_GLOBAL_SEARCH_PAGINATION = {
  purchases: { page: 1, total: 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
  maintenance: { page: 1, total: 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
  bills: { page: 1, total: 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
  salary: { page: 1, total: 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
};

const formatDateValue = (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
const addDays = (dateStr, days) => {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  return formatDateValue(d);
};
const formatSalaryCurrency = (value) => formatUiCurrencyCompact(roundSalaryDisplayAmount(value));
const formatDate = formatUiDate;
const formatDateWithWeekday = formatUiDate;
const formatMoneyCompact = (value) => formatUiCurrencyCompact(value);
const formatMoneyDetailed = (value) => formatUiCurrencyDetailed(value);
const formatSalaryItemCurrency = (value) => formatUiCurrencyDetailed(value);
const fmtK = (value) => {
  const n = roundSalaryDisplayAmount(value);
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toFixed(0);
};
const shortStaffCalendarName = (name) => String(name || '').trim().split(/\s+/)[0] || '';
const renderSalaryCalendarEntries = (entries, toneClass, marker, limit = 2) => {
  if (!entries?.length) return null;
  return (
    <>
      {entries.slice(0, limit).map((entry, index) => (
        <div key={`${marker}-${entry.id || entry.staffName}-${index}`} className={`text-[9px] font-semibold leading-tight truncate ${toneClass}`}>
          {marker} {shortStaffCalendarName(entry.staffName)} {fmtK(entry.amountNumber)}
          {entry.rowType !== 'due' ? ` (${formatSalaryEventDue(entry, fmtK)})` : ''}
        </div>
      ))}
      {entries.length > limit && (
        <div className={`text-[9px] leading-tight ${toneClass}`}>+{entries.length - limit} more</div>
      )}
    </>
  );
};
const staffName = (staffMember) => (
  staffMember?.full_name || `${staffMember?.first_name || ''} ${staffMember?.last_name || ''}`.trim()
);
const NAME_BASED_EXPENSE_TABS = ['maintenance', 'bills'];

const isNameBasedTab = (tab) => NAME_BASED_EXPENSE_TABS.includes(tab);

const formatCategoryLabel = (category) => String(category || '')
  .replace(/^raw_material$/, 'inventory')
  .split('_')
  .filter(Boolean)
  .map(part => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const getInventoryUnitAbbr = (item) => {
  if (!item) return '';
  if (item.unit_abbr) return item.unit_abbr;
  if (item.unit?.abbreviation) return item.unit.abbreviation;
  if (typeof item.unit === 'string') return item.unit;
  return item.unit_name || '';
};

function Expenses() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuthStore();
  const [expenses, setExpenses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [salaryStaffFilter, setSalaryStaffFilter] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState(EMPTY_GLOBAL_SEARCH_RESULTS);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [collapsedGlobalSearchGroups, setCollapsedGlobalSearchGroups] = useState({ purchases: false, maintenance: false, bills: false, salary: false });
  const [globalSearchPagination, setGlobalSearchPagination] = useState(EMPTY_GLOBAL_SEARCH_PAGINATION);
  const [expenseTab, setExpenseTab] = useState('all');
  const [viewMode, setViewMode] = useState('calendar');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPurchaseDepartment, setFilterPurchaseDepartment] = useState('');
  const [filterPurchaseSupplier, setFilterPurchaseSupplier] = useState('');
  const [filterPurchaseItem, setFilterPurchaseItem] = useState('');
  const [filterExpenseName, setFilterExpenseName] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
  const [invoiceImage, setInvoiceImage] = useState(null);
  const [pendingExpenseEntries, setPendingExpenseEntries] = useState([]);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [historyModal, setHistoryModal] = useState(null); // { loading, data, title }

  const openHistoryModal = async (expense) => {
    setHistoryModal({ loading: true, data: null, title: expense.name || expense.description || 'History' });
    try {
      const res = await api.get(`/expenses/${expense.id}/history/`);
      setHistoryModal({ loading: false, data: res.data, title: res.data?.item_name || 'History' });
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load history'));
      setHistoryModal(null);
    }
  };

  const goToInventoryItem = (expense) => {
    // Resolve the inventory item id via the same matcher the table uses
    const match = getInventoryMatch(expense);
    const lineItem = (expense.line_items || []).find(li => li.raw_material);
    const rmId = match?.id || lineItem?.raw_material || expense.inventory_item_id;
    if (rmId) {
      navigate(`/inventory?tab=inventory&item_id=${rmId}`);
    } else {
      navigate('/inventory?tab=inventory');
    }
  };
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseNameOptions, setExpenseNameOptions] = useState({
    maintenance: [],
    bills: [],
  });
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    // Set default to This Month
    const firstDay = formatDateValue(new Date(today.getFullYear(), today.getMonth(), 1));
    const lastDay = formatDateValue(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    return { start: firstDay, end: lastDay };
  });
  const [dateRangePreset, setDateRangePreset] = useState('this_month');
  const [fromTime, setFromTime] = useState('06:00');
  const [toTime, setToTime] = useState('05:59');
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  const [collapsedExpenseBreakdown, setCollapsedExpenseBreakdown] = useState(new Set());
  const [collapsedSalarySummaryStaff, setCollapsedSalarySummaryStaff] = useState(new Set());
  const [salaryOverview, setSalaryOverview] = useState({ summary: {}, staff_summary: [], event_rows: [] });
  const [salaryOverviewLoading, setSalaryOverviewLoading] = useState(false);
  const handledExpenseNumberRef = useRef('');
  const [businessStartHour, setBusinessStartHour] = useState(6);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [lifecycleEvents, setLifecycleEvents] = useState([]);
  const [offDayRecords, setOffDayRecords] = useState([]);
  const filterEndCalendarDate = dateRange.end ? addDays(dateRange.end, 1) : '';
  const currentBusinessDate = useMemo(() => {
    const current = new Date();
    if (current.getHours() < businessStartHour) {
      current.setDate(current.getDate() - 1);
    }
    return formatDateValue(current);
  }, [businessStartHour]);

  const shiftExpenseRange = (delta) => {
    const shifted = shiftDateRangeByPreset({
      preset: dateRangePreset,
      startDate: dateRange.start,
      endDate: dateRange.end,
      delta,
    });
    // shiftDateRangeByPreset returns { startDate, endDate }; this page stores { start, end }.
    setDateRange({ start: shifted.startDate, end: shifted.endDate });
  };
  
  const [formData, setFormData] = useState({
    date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    category: 'miscellaneous',
    expense_name: '',
    department: '',
    amount: '',
    description: '',
    payment_method: 'cash',
    reference: '',
    is_monthly_fixed: false,
    vendor_name: '',
    vendor_contact: '',
    notes: '',
    inventory_item: '',
    qty: '',
    payment_type: 'salary', // For salary tab
    staff_id: '',
  });

  // When editing, the edit form/submit must follow the RECORD's type (so a salary/purchase/bill
  // edited from the "All" tab opens that type's popup), not the active tab. Falls back to the
  // active tab when adding.
  const formTab = editingExpense
    ? ({ salary: 'salary', raw_material: 'purchases', maintenance: 'maintenance', bills: 'bills' }[formData.category] || 'all')
    : expenseTab;

  const [summary, setSummary] = useState({
    total: 0,
    cash: 0,
    digital: 0,
    byCategory: {}
  });

  // Smart paste state
  const [showSmartPaste, setShowSmartPaste] = useState(false);
  const [smartPasteStep, setSmartPasteStep] = useState('input'); // 'input' or 'review'
  const [smartPasteText, setSmartPasteText] = useState('');
  const [smartPasteData, setSmartPasteData] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [smartPasteSaving, setSmartPasteSaving] = useState(false);
  const purchaseSupplierOptions = useMemo(() => {
    const supplierMap = new Map();
    inventoryItems.forEach(item => {
      if (item.supplier && item.supplier_name) supplierMap.set(String(item.supplier), item.supplier_name);
    });
    return Array.from(supplierMap, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [inventoryItems]);
  const purchaseItemOptions = useMemo(
    () => [...inventoryItems]
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      .map(item => ({ id: item.id, name: item.name })),
    [inventoryItems]
  );

  useEffect(() => {
    fetchDepartments();
    fetchExpenseNames();
    fetchSalaryData();
    fetchInventory();
    fetchPublicHolidays();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const view = params.get('view');
    const search = params.get('search');
    const itemId = params.get('item_id');
    const start = params.get('start_date');
    const end = params.get('end_date');

    if (tab && EXPENSE_TABS.some(entry => entry.id === tab)) setExpenseTab(tab);
    if (view === 'table' || view === 'calendar') setViewMode(view);
    if (search !== null) setSearchTerm(search);
    if (itemId !== null) setFilterPurchaseItem(itemId);
    if (start && end) setDateRange({ start, end });
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const expenseNumber = params.get('expense_number');
    if (!expenseNumber || expenseTab !== 'purchases' || !expenses.length) return;
    const handleKey = `${expenseNumber}:${dateRange.start || ''}:${dateRange.end || ''}`;
    if (handledExpenseNumberRef.current === handleKey) return;
    const matchedExpense = expenses.find(expense => String(expense.expense_number || '') === String(expenseNumber));
    if (!matchedExpense) return;
    handledExpenseNumberRef.current = handleKey;
    setViewMode('table');
    openEditModal(matchedExpense);
  }, [location.search, expenseTab, expenses, dateRange.start, dateRange.end]);

  useEffect(() => {
    fetchExpenses();
  }, [dateRange, expenseTab, filterCategory, filterPaymentMethod]);

  useEffect(() => {
    if (expenseTab === 'purchases' || expenseTab === 'all') {
      fetchInventory();
    }
    if (isNameBasedTab(expenseTab)) {
      fetchExpenseNames();
    }
  }, [expenseTab]);

  useEffect(() => {
    if (showModal) return;
    if (expenseTab === 'purchases' || expenseTab === 'all') {
      fetchInventory();
    }
    if (isNameBasedTab(expenseTab)) {
      fetchExpenseNames();
    }
  }, [showModal, expenseTab]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings/');
        if (res.data?.business_day_start_hour !== undefined) {
          setBusinessStartHour(res.data.business_day_start_hour);
          const startHour = String(res.data.business_day_start_hour).padStart(2, '0');
          setFromTime(`${startHour}:00`);
          setToTime(`${String(res.data.business_day_start_hour - 1 >= 0 ? res.data.business_day_start_hour - 1 : 23).padStart(2, '0')}:59`);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, [setFromTime, setToTime]);

  useEffect(() => {
    if (dateRange.start) {
      const d = new Date(`${dateRange.start}T12:00:00`);
      if (!Number.isNaN(d.getTime())) {
        setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
      }
    }
  }, [dateRange.start]);

  useEffect(() => {
    if (viewMode !== 'calendar') return;
    if (['this_month', 'this_year', 'custom'].includes(dateRangePreset)) return;
    const today = new Date();
    setDateRange({
      start: formatDateValue(new Date(today.getFullYear(), today.getMonth(), 1)),
      end: formatDateValue(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    });
    setDateRangePreset('this_month');
  }, [viewMode, dateRangePreset]);

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/departments/?page_size=500');
      setDepartments(res.data.results || res.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchExpenseNames = async () => {
    try {
      const res = await api.get('/expense-names/?page_size=1000');
      const rows = res.data.results || res.data || [];
      setExpenseNameOptions({
        maintenance: rows.filter(row => row.expense_type === 'maintenance').sort((a, b) => a.name.localeCompare(b.name)),
        bills: rows.filter(row => row.expense_type === 'bills').sort((a, b) => a.name.localeCompare(b.name)),
      });
      return rows;
    } catch (error) {
      console.error('Error fetching expense names:', error);
      return [];
    }
  };

  const fetchSalaryData = async () => {
    try {
      const [staffRows, advanceRows, lifecycleEvents, perfRes] = await Promise.all([
        fetchPaginatedCollection(api, '/staff/', { pageSize: 500 }).catch(() => []),
        fetchPaginatedCollection(api, '/staff-advances/', { pageSize: 500, maxRecords: 50000 }).catch(() => []),
        fetchPaginatedCollection(api, '/staff-lifecycle-events/', { pageSize: 500, maxRecords: 10000 }).catch(() => []),
        api.get('/staff-performance/?page_size=2000').catch(() => ({ data: [] })),
      ]);
      setStaff(mergeStaffWithLifecycleHistory(staffRows, lifecycleEvents));
      setLifecycleEvents(Array.isArray(lifecycleEvents) ? lifecycleEvents : (lifecycleEvents?.results || []));
      const perfRows = perfRes.data?.results || perfRes.data || [];
      setOffDayRecords((Array.isArray(perfRows) ? perfRows : []).filter(p => ['leave', 'holiday', 'absentee'].includes(p.violation_type)));
      setSalaryPayments(advanceRows);
    } catch (error) {
      console.error('Error fetching salary data:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      setLoadingInventory(true);
      const res = await api.get('/raw-materials/?page_size=1000');
      const items = res.data.results || res.data || [];
      setInventoryItems(items);
      return items;
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to fetch inventory items');
      return [];
    } finally {
      setLoadingInventory(false);
    }
  };

  const fetchPublicHolidays = async () => {
    try {
      const res = await api.get('/public-holidays/?page_size=500');
      setPublicHolidays(res.data.results || res.data || []);
    } catch (error) {
      console.error('Error fetching public holidays:', error);
    }
  };

  const handleSmartPaste = () => {
    setShowSmartPaste(true);
    setSmartPasteStep('input');
    setSmartPasteText('');
    setSmartPasteData([]);
  };

  const processSmartPasteText = async () => {
    const text = smartPasteText.trim();
    if (!text) {
      toast.error('Please enter or paste text');
      return;
    }

    const lines = text.trim().split('\n');
    const parsedData = [];
    let parsedDate = '';

    const MONTHS = { jan:0, feb:1, mar:2, apr:3, may:4, jun:5, jul:6, aug:7, sep:8, oct:9, nov:10, dec:11,
      january:0, february:1, march:2, april:3, june:5, july:6, august:7, september:8, october:9, november:10, december:11 };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || /^=+$/.test(line)) continue;

      // Try to detect date line (e.g. "02 May 2026" or "2026-05-02")
      if (!parsedDate) {
        const dm = line.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
        if (dm) {
          const mIdx = MONTHS[dm[2].toLowerCase()];
          if (mIdx !== undefined) {
            parsedDate = `${dm[3]}-${String(mIdx + 1).padStart(2, '0')}-${String(parseInt(dm[1], 10)).padStart(2, '0')}`;
            continue;
          }
        }
        const iso = line.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (iso) { parsedDate = line; continue; }
      }

      // Split by tab, comma, or 2+ spaces
      let parts;
      if (line.includes('\t')) parts = line.split('\t');
      else if (line.includes(',')) parts = line.split(',');
      else parts = line.split(/\s{2,}/);
      parts = parts.map(p => p.trim());

      if (parts.length < 3) continue;

      const itemName = parts[0];
      // parts[1] = qty + units, e.g. "1000 pcs", "3 L", "15 kg"
      const qtyUnitMatch = parts[1].match(/^([\d.]+)\s*([A-Za-z]*)$/);
      const qty = qtyUnitMatch ? parseFloat(qtyUnitMatch[1]) || 0 : (parseFloat(parts[1].replace(/[^0-9.]/g, '')) || 0);
      const units = qtyUnitMatch ? (qtyUnitMatch[2] || '') : '';
      const totalCost = parseFloat((parts[2] || '').replace(/[^0-9.]/g, '')) || 0;
      const notes = parts[3] || '';
      const vendorName = parts[4] || '';
      const vendorContact = parts[5] || '';

      if (!itemName || qty <= 0 || totalCost <= 0) continue;

      parsedData.push({
        id: Date.now() + Math.random(),
        itemName,
        qty,
        units,
        totalCost,
        notes,
        vendorName,
        vendorContact,
        matchedItem: null
      });
    }

    if (parsedData.length === 0) {
      toast.error('No valid data found');
      return;
    }

    if (parsedDate) {
      setFormData(prev => ({ ...prev, date: parsedDate }));
    }

    // If Inventory tab, match with inventory
    if (expenseTab === 'purchases') {
      const items = inventoryItems.length > 0 ? inventoryItems : await fetchInventory();
      const matchedData = parsedData.map(item => {
        const lcName = item.itemName.toLowerCase();
        const match = items.find(inv =>
          inv.name.toLowerCase() === lcName ||
          inv.name.toLowerCase().includes(lcName) ||
          lcName.includes(inv.name.toLowerCase())
        );
        return {
          ...item,
          matchedItem: match || null,
          units: getInventoryUnitAbbr(match) || item.units || ''
        };
      });
      setSmartPasteData(matchedData);
      setSmartPasteStep('review');
    } else {
      setSmartPasteData(parsedData);
      setSmartPasteStep('review');
    }
  };

  const saveSmartPasteExpenses = async () => {
    try {
      setSmartPasteSaving(true);
      const activeTab = EXPENSE_TABS.find(tab => tab.id === expenseTab);
      
      for (const item of smartPasteData) {
        if (expenseTab === 'purchases' && !item.matchedItem) {
          toast.error(`Skipping "${item.itemName}" - no matching inventory item found`);
          continue;
        }

        const expenseData = {
          date: formData.date,
          category: expenseTab === 'purchases' ? 'raw_material' :
                   expenseTab === 'maintenance' ? 'maintenance' :
                   expenseTab === 'bills' ? 'bills' :
                   expenseTab === 'salary' ? 'salary' : 'miscellaneous',
          amount: (item.totalCost || item.totalAmount).toString(),
          name: isNameBasedTab(expenseTab) ? (item.itemName || item.description || '') : '',
          description: expenseTab === 'purchases' 
            ? `${item.matchedItem?.name || item.itemName} - Qty ${item.qty}${getInventoryUnitAbbr(item.matchedItem) || item.units ? ` ${getInventoryUnitAbbr(item.matchedItem) || item.units}` : ''}`
            : item.itemName || item.description || '',
          payment_method: formData.payment_method,
          reference: formData.reference,
          department: item.matchedItem?.department || formData.department || '',
          vendor_name: item.vendorName || '',
          vendor_contact: item.vendorContact || '',
          notes: item.notes || '',
        };

        if (expenseTab === 'purchases' && item.matchedItem) {
          expenseData.department = item.matchedItem.department || '';
      // Link the inventory expense to the inventory item so the backend
          // increments stock and bumps quantity_updated_at.
          if (item.qty) {
            expenseData.inventory_item_id = item.matchedItem.id;
            expenseData.inventory_qty = parseFloat(item.qty);
          }
        }

        await api.post('/expenses/', expenseData);
      }

      toast.success(`${smartPasteData.length} expenses added successfully`);
      if (isNameBasedTab(expenseTab)) fetchExpenseNames();
      setShowSmartPaste(false);
      setSmartPasteData([]);
      setSmartPasteStep('input');
      setSmartPasteText('');
      fetchExpenses();
    } catch (error) {
      console.error('Error saving smart paste expenses:', error);
      toast.error(formatApiError(error, 'Failed to save expenses'));
    } finally {
      setSmartPasteSaving(false);
    }
  };

  const updateSmartPasteItem = (id, field, value) => {
    setSmartPasteData(prev => prev.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'qty' || field === 'unitPrice') {
          updated.totalAmount = (updated.qty || 0) * (updated.unitPrice || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  const deleteSmartPasteItem = (id) => {
    setSmartPasteData(prev => prev.filter(item => item.id !== id));
  };

  const handleAddExpenseName = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!isNameBasedTab(expenseTab)) return;
    const enteredName = window.prompt('Enter category:');
    if (enteredName === null) return;
    const cleanName = enteredName.trim();
    if (!cleanName) return;

    try {
      const existing = (expenseNameOptions[expenseTab] || []).find(row => row.name.toLowerCase() === cleanName.toLowerCase());
      if (existing) {
        setFormData(prev => ({ ...prev, expense_name: existing.name }));
        return;
      }
      const res = await api.post('/expense-names/', {
        expense_type: expenseTab,
        name: cleanName,
        is_active: true,
      });
      setExpenseNameOptions(prev => ({
        ...prev,
        [expenseTab]: [...(prev[expenseTab] || []), res.data].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      setFormData(prev => ({ ...prev, expense_name: res.data.name }));
      toast.success('Category added');
      fetchExpenseNames();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to add category'));
    }
  };

  const handleEditExpenseName = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!isNameBasedTab(expenseTab) || !formData.expense_name) return;
    const currentOption = (expenseNameOptions[expenseTab] || []).find(row => row.name === formData.expense_name);
    if (!currentOption) {
      toast.error('Select a saved category first');
      return;
    }
    const enteredName = window.prompt('Edit category:', formData.expense_name);
    if (enteredName === null) return;
    const cleanName = enteredName.trim();
    if (!cleanName) return;

    try {
      const res = await api.patch(`/expense-names/${currentOption.id}/`, { name: cleanName });
      setExpenseNameOptions(prev => ({
        ...prev,
        [expenseTab]: (prev[expenseTab] || [])
          .map(row => row.id === currentOption.id ? res.data : row)
          .sort((a, b) => a.name.localeCompare(b.name)),
      }));
      setFormData(prev => ({ ...prev, expense_name: res.data.name }));
      toast.success('Category updated');
      fetchExpenseNames();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update category'));
    }
  };

  const handleDeleteExpenseName = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!isNameBasedTab(expenseTab) || !formData.expense_name) return;
    const currentOption = (expenseNameOptions[expenseTab] || []).find(row => row.name === formData.expense_name);
    if (!currentOption) {
      toast.error('Select a saved category first');
      return;
    }
    if (!window.confirm(`Delete "${currentOption.name}" from saved categories? Existing expenses will stay unchanged.`)) return;

    try {
      await api.delete(`/expense-names/${currentOption.id}/`);
      setExpenseNameOptions(prev => ({
        ...prev,
        [expenseTab]: (prev[expenseTab] || []).filter(row => row.id !== currentOption.id),
      }));
      setFormData(prev => ({ ...prev, expense_name: '' }));
      toast.success('Category deleted');
      fetchExpenseNames();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete category'));
    }
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page_size', '2000');
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      const activeTab = EXPENSE_TABS.find(tab => tab.id === expenseTab);
      const singleTabCategory = activeTab?.categories?.length === 1 ? activeTab.categories[0] : '';
      if (filterCategory) params.append('category', filterCategory);
      else if (singleTabCategory && expenseTab !== 'salary' && expenseTab !== 'all') params.append('category', singleTabCategory);
      if (filterPaymentMethod) params.append('payment_method', filterPaymentMethod);
      
      const response = await api.get(`/expenses/?${params.toString()}`);
      const data = response.data.results || response.data;
      setExpenses(data);
      
      // Extract unique categories from expenses
      const categoryMap = new Map([
        ['raw_material', 'Inventory'],
        ['maintenance', 'Maintenance'],
        ['bills', 'Bills'],
        ['salary', 'Salary'],
      ]);
      data.forEach(expense => {
        if (!expense.category) return;
        categoryMap.set(expense.category, expense.category_display || formatCategoryLabel(expense.category));
      });
      setExpenseCategories(Array.from(categoryMap, ([value, label]) => ({ value, label })));
      
      // Calculate summary
      const total = data.reduce((sum, e) => sum + parseFloat(e.amount), 0);
      const cash = data.filter(e => e.payment_method === 'cash')
                       .reduce((sum, e) => sum + parseFloat(e.amount), 0);
      const digital = data.filter(e => e.payment_method !== 'cash')
                          .reduce((sum, e) => sum + parseFloat(e.amount), 0);
      
      const byCategory = {};
      data.forEach(e => {
        byCategory[e.category] = (byCategory[e.category] || 0) + parseFloat(e.amount);
      });
      
      setSummary({ total, cash, digital, byCategory });
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to fetch expenses'));
    } finally {
      setLoading(false);
    }
  };

  const buildExpenseEntry = (sourceForm = formData, sourceTab = formTab, sourceImage = invoiceImage) => {
    let submitData = { ...sourceForm };
    const selectedExpenseName = String(submitData.expense_name || '').trim();
    delete submitData.expense_name;

    if (!submitData.date) { toast.error('Select a date'); return null; }
    if (submitData.amount === '') { toast.error('Enter amount'); return null; }

    if (sourceTab === 'purchases') {
      const selectedItem = inventoryItems.find(i => String(i.id) === String(sourceForm.inventory_item));
      if (!selectedItem) { toast.error('Select inventory item'); return null; }
      if (!sourceForm.qty) { toast.error('Enter quantity'); return null; }
      submitData.category = 'raw_material';
      const unitAbbr = getInventoryUnitAbbr(selectedItem);
      submitData.description = sourceForm.qty
        ? `${selectedItem?.name || sourceForm.description} - Qty ${sourceForm.qty}${unitAbbr ? ` ${unitAbbr}` : ''}`
        : (selectedItem?.name || sourceForm.description);
      submitData.department = selectedItem?.department || '';
      submitData.amount = sourceForm.amount;
      submitData.inventory_item_id = selectedItem.id;
      submitData.inventory_qty = parseFloat(sourceForm.qty);
      delete submitData.inventory_item;
      delete submitData.qty;
    } else if (sourceTab === 'salary') {
      const selectedStaff = staff.find(s => String(s.id) === String(sourceForm.staff_id));
      if (!selectedStaff) { toast.error('Staff not found'); return null; }
      return {
        _key: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        saveType: 'salary',
        tab: sourceTab,
        date: sourceForm.date,
        amount: Number(sourceForm.amount || 0),
        label: `${selectedStaff.full_name || `${selectedStaff.first_name || ''} ${selectedStaff.last_name || ''}`.trim()} - ${sourceForm.payment_type || 'salary'}`,
        data: {
          staff: selectedStaff.id,
          advance_date: sourceForm.date,
          amount: sourceForm.amount,
          payment_type: sourceForm.payment_type || 'salary',
          reason: sourceForm.notes || '',
          status: 'approved',
        },
      };
    } else {
      if (isNameBasedTab(sourceTab)) {
        if (!selectedExpenseName) { toast.error('Please select or add a category'); return null; }
        submitData.category = sourceTab === 'maintenance' ? 'maintenance' : 'bills';
        submitData.name = selectedExpenseName;
        submitData.description = String(submitData.description || '').trim() || selectedExpenseName;
      } else if (sourceTab === 'maintenance') {
        submitData.category = 'maintenance';
      } else if (sourceTab === 'bills') {
        submitData.category = 'bills';
      }
      delete submitData.inventory_item;
      delete submitData.qty;
      delete submitData.payment_type;
    }

    if (!submitData.department) {
      if (editingExpense) submitData.department = null;
      else delete submitData.department;
    }

    return {
      _key: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      saveType: 'expense',
      tab: sourceTab,
      date: submitData.date,
      amount: Number(submitData.amount || 0),
      label: submitData.name || submitData.description || selectedExpenseName || submitData.category || 'Expense',
      data: submitData,
      invoiceImage: sourceImage,
    };
  };

  const saveExpenseEntry = async (entry, editTarget = null) => {
    if (entry.saveType === 'salary') {
      if (editTarget?.originalId) {
        await api.patch(`/staff-advances/${editTarget.originalId}/`, entry.data);
      } else {
        await api.post('/staff-advances/', entry.data);
      }
      return;
    }

    const res = editTarget
      ? await api.patch(`/expenses/${editTarget.id}/`, entry.data)
      : await api.post('/expenses/', entry.data);
    const expId = res.data.id || editTarget?.id;
    if (entry.invoiceImage && expId) {
      const fd = new FormData();
      fd.append('model_type', 'expense');
      fd.append('object_id', expId);
      fd.append('image', entry.invoiceImage);
      fd.append('field_name', 'invoice_image');
      await api.post('/upload-image/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
  };

  const handleApplyExpensePending = () => {
    const entry = buildExpenseEntry();
    if (!entry) return;
    setPendingExpenseEntries(prev => [...prev, entry]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const entries = editingExpense
      ? [buildExpenseEntry()]
      : (pendingExpenseEntries.length > 0 ? pendingExpenseEntries : [buildExpenseEntry()]);
    if (entries.some(entry => !entry)) return;
    try {
      await Promise.all(entries.map(entry => saveExpenseEntry(entry, editingExpense)));
      toast.success(editingExpense ? 'Expense updated' : `${entries.length} expense${entries.length === 1 ? '' : 's'} saved`);
      setPendingExpenseEntries([]);
      setShowModal(false);
      resetForm();
      if (entries.some(entry => entry.saveType === 'salary')) fetchSalaryData();
      if (entries.some(entry => entry.saveType === 'expense')) fetchExpenses();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save expense'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/expenses/${id}/`);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete expense'));
    }
  };

  const handleDeleteAllForDate = async (date, expenses) => {
    if (!window.confirm(`Delete all ${expenses.length} expenses for ${formatExpenseDateHeader(date)}?`)) return;
    try {
      await Promise.all(expenses.map((e) => {
        if (e.source_type === 'salary_payment') {
          const actualId = String(e.salary_row?.originalId || e.originalId || e.id).replace(/^(paid|advance|bonus|deduction|payment)-/, '');
          return api.delete(`/staff-advances/${actualId}/`);
        }
        return api.delete(`/expenses/${e.id}/`);
      }));
      toast.success(`${expenses.length} expenses deleted`);
      fetchExpenses();
      fetchSalaryData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete expenses'));
    }
  };

  const getSalaryRowActualId = (rowOrId) => String(
    typeof rowOrId === 'object'
      ? (rowOrId?.originalId || rowOrId?.id || '')
      : (rowOrId || '')
  ).replace(/^(paid|advance|bonus|deduction|payment)-/, '');

  const handleSalaryDelete = async (id) => {
    if (!window.confirm('Delete this salary payment?')) return;
    try {
      const actualId = getSalaryRowActualId(id);
      await api.delete(`/staff-advances/${actualId}/`);
      toast.success('Salary payment deleted');
      fetchSalaryData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete salary payment'));
    }
  };

  const openSalaryEditModal = (row) => {
    const actualId = getSalaryRowActualId(row);
    const paymentType = row.type === 'paid' ? 'salary' : row.type;
    setPendingExpenseEntries([]);
    
    setEditingExpense({
      ...row,
      originalId: actualId,
    });
    setFormData({
      date: row.date,
      category: 'salary',
      expense_name: '',
      department: row.department || '',
      amount: row.amount.toString(),
      description: row.staffName,
      staff_id: row.staffId || row.staff || '',
      payment_method: 'cash',
      reference: '',
      is_monthly_fixed: false,
      vendor_name: '',
      vendor_contact: '',
      notes: row.note || '',
      inventory_item: '',
      qty: '',
      payment_type: paymentType,
    });
    setInvoiceImage(null);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
      category: expenseTab === 'purchases' ? 'raw_material' :
               expenseTab === 'maintenance' ? 'maintenance' :
               expenseTab === 'bills' ? 'bills' :
               expenseTab === 'salary' ? 'salary' : 'miscellaneous',
      expense_name: '',
      department: '',
      amount: '',
      description: '',
      payment_method: 'cash',
      reference: '',
      is_monthly_fixed: false,
      vendor_name: '',
      vendor_contact: '',
      notes: '',
      inventory_item: '',
      qty: '',
      payment_type: 'salary',
      staff_id: '',
    });
    setInvoiceImage(null);
  };

  const closeExpenseModal = () => {
    setShowModal(false);
    setPendingExpenseEntries([]);
    resetForm();
  };

  const openEditModal = (expense) => {
    setPendingExpenseEntries([]);
    setEditingExpense(expense);
    const baseFormData = {
      date: expense.date,
      category: expense.category,
      expense_name: getExpenseDisplayName(expense),
      department: expense.department || '',
      amount: expense.amount,
      description: expense.description,
      payment_method: expense.payment_method,
      reference: expense.reference || '',
      is_monthly_fixed: expense.is_monthly_fixed || false,
      vendor_name: expense.vendor_name || '',
      vendor_contact: expense.vendor_contact || '',
      notes: expense.notes || '',
    };

    // For inventory expenses, try to populate inventory_item and qty from line_items or description
    if (expense.category === 'raw_material') {
      const primaryLine = getPrimaryInventoryLine(expense);
      const parsed = parsePurchaseDesc(expense.description);
      const invMatch = getInventoryMatch(expense);
      if (invMatch?.id) {
        baseFormData.inventory_item = String(invMatch.id);
      }
      if (primaryLine?.quantity) {
        baseFormData.qty = primaryLine.quantity || '';
      } else if (parsed.qtyText) {
        const qtyMatch = parsed.qtyText.match(/^([\d.]+)/);
        baseFormData.qty = qtyMatch ? qtyMatch[1] : '';
      }
    }

    setFormData(baseFormData);
    setInvoiceImage(null);
    setShowModal(true);
  };

  const selectedExpenseTab = EXPENSE_TABS.find(tab => tab.id === expenseTab) || EXPENSE_TABS[0];

  const staffById = useMemo(() => {
    const map = {};
    staff.forEach(member => { map[String(member.id)] = member; });
    return map;
  }, [staff]);

  const holidayMap = useMemo(() => {
    const map = {};
    publicHolidays.forEach((holiday) => {
      const start = holiday.start_date;
      const end = holiday.end_date || holiday.start_date;
      if (!start) return;
      let cursor = new Date(`${start}T12:00:00`);
      const endDate = new Date(`${end}T12:00:00`);
      while (!Number.isNaN(cursor.getTime()) && cursor <= endDate) {
        map[formatDateValue(cursor)] = holiday;
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [publicHolidays]);

  // Employment / re-employment / termination events keyed by `${staff}_${YYYY-MM-DD}`.
  const lifecycleMap = useMemo(() => {
    const m = {};
    (lifecycleEvents || []).forEach(e => {
      if (!['employed', 'reemployed', 'terminated'].includes(e.event_type)) return;
      const d = String(e.event_date || '').substring(0, 10);
      if (!d) return;
      (m[`${e.staff}_${d}`] = m[`${e.staff}_${d}`] || []).push(e);
    });
    return m;
  }, [lifecycleEvents]);

  // Day-off (leave/holiday/absentee) records keyed by `${staff}_${YYYY-MM-DD}`.
  const offDayMap = useMemo(() => {
    const m = {};
    (offDayRecords || []).forEach(r => {
      const d = String(r.date || '').substring(0, 10);
      if (d) m[`${r.staff}_${d}`] = r;
    });
    return m;
  }, [offDayRecords]);

  const getHolidayForDate = (dateStr) => holidayMap[dateStr] || null;

  const getSalaryStaffName = (record) => {
    const member = staffById[String(record.staff)];
    return record.staff_name || staffName(member) || 'Unknown Staff';
  };

  const matchesSalarySearch = (values) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return values.some(value => String(value || '').toLowerCase().includes(q));
  };

  const filteredExpenses = useMemo(() => {
    // If global search is active, use search results for the current tab
    if (globalSearchQuery) {
      const searchResultsByTab = {
        purchases: globalSearchResults.purchases
          .map(e => ({ ...e, category: 'raw_material' }))
          .filter(matchesPurchaseFilters),
        maintenance: globalSearchResults.maintenance.map(e => ({ ...e, category: 'maintenance' })),
        bills: globalSearchResults.bills.map(e => ({ ...e, category: 'bills' })),
      };
      if (expenseTab === 'all') {
        return [
          ...searchResultsByTab.purchases,
          ...searchResultsByTab.maintenance,
          ...searchResultsByTab.bills,
        ];
      }
      return searchResultsByTab[expenseTab] || [];
    }

    // Otherwise use normal filtering
    const q = searchTerm.trim().toLowerCase();
    return expenses.filter(expense => {
      const tabMatch = !selectedExpenseTab.categories || selectedExpenseTab.categories.includes(expense.category);
      if (!tabMatch) return false;
      if (expenseTab === 'purchases' && !matchesPurchaseFilters(expense)) return false;
      if (isNameBasedTab(expenseTab) && filterExpenseName && getExpenseDisplayName(expense) !== filterExpenseName) return false;
      if (!q) return true;
      return [
        expense.description,
        expense.name,
        expense.expense_number,
        expense.department_name,
        expense.payment_method_display,
        expense.payment_method,
        getExpenseCategoryLabel(expense),
        expense.amount,
      ].some(value => String(value || '').toLowerCase().includes(q));
    });
  }, [expenses, selectedExpenseTab, expenseTab, filterExpenseName, filterPurchaseDepartment, filterPurchaseSupplier, filterPurchaseItem, inventoryItems, searchTerm, globalSearchQuery, globalSearchResults]);

  const toggleDate = (date) => {
    setCollapsedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const expandAllDates = () => setCollapsedDates(new Set());
  const collapseAllDates = () => setCollapsedDates(new Set(groupedExpenses.map(([date]) => date)));

  const toggleExpenseBreakdownSection = (sectionId) => {
    setCollapsedExpenseBreakdown(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const salaryPaymentRows = useMemo(() => {
    const start = dateRange.start || '0000-01-01';
    const end = dateRange.end || '9999-12-31';
    const paidRows = salaryPayments
      .filter(payment => payment.payment_type === 'salary')
      .filter(payment => payment.advance_date >= start && payment.advance_date <= end)
      .map(payment => ({
        id: `paid-${payment.id}`,
        type: 'paid',
        date: payment.advance_date,
        staffId: payment.staff,
        staffName: getSalaryStaffName(payment),
        department: staffById[String(payment.staff)]?.department_name || '',
        amount: parseFloat(payment.amount || 0),
        status: payment.status,
        note: payment.reason || payment.payment_type_label || 'Salary payment',
      }))
      .filter(row => matchesSalarySearch([row.staffName, row.department, row.amount, row.status, row.note]));

    const advanceRows = salaryPayments
      .filter(payment => payment.payment_type === 'advance')
      .filter(payment => payment.advance_date >= start && payment.advance_date <= end)
      .map(payment => ({
        id: `advance-${payment.id}`,
        type: 'advance',
        date: payment.advance_date,
        staffId: payment.staff,
        staffName: getSalaryStaffName(payment),
        department: staffById[String(payment.staff)]?.department_name || '',
        amount: parseFloat(payment.amount || 0),
        status: payment.status,
        note: payment.reason || payment.payment_type_label || 'Advance payment',
      }))
      .filter(row => matchesSalarySearch([row.staffName, row.department, row.amount, row.status, row.note]));

    const bonusRows = salaryPayments
      .filter(payment => payment.payment_type === 'bonus')
      .filter(payment => payment.advance_date >= start && payment.advance_date <= end)
      .map(payment => ({
        id: `bonus-${payment.id}`,
        type: 'bonus',
        date: payment.advance_date,
        staffId: payment.staff,
        staffName: getSalaryStaffName(payment),
        department: staffById[String(payment.staff)]?.department_name || '',
        amount: parseFloat(payment.amount || 0),
        status: payment.status,
        note: payment.reason || payment.payment_type_label || 'Bonus payment',
      }))
      .filter(row => matchesSalarySearch([row.staffName, row.department, row.amount, row.status, row.note]));

    const giftRows = salaryPayments
      .filter(payment => payment.payment_type === 'gift')
      .filter(payment => payment.advance_date >= start && payment.advance_date <= end)
      .map(payment => ({
        id: `gift-${payment.id}`,
        type: 'gift',
        date: payment.advance_date,
        staffId: payment.staff,
        staffName: getSalaryStaffName(payment),
        department: staffById[String(payment.staff)]?.department_name || '',
        amount: parseFloat(payment.amount || 0),
        status: payment.status,
        note: payment.reason || payment.payment_type_label || 'Gift',
      }))
      .filter(row => matchesSalarySearch([row.staffName, row.department, row.amount, row.status, row.note]));

    const deductionRows = salaryPayments
      .filter(payment => payment.payment_type === 'deduction')
      .filter(payment => payment.advance_date >= start && payment.advance_date <= end)
      .map(payment => ({
        id: `deduction-${payment.id}`,
        type: 'deduction',
        date: payment.advance_date,
        staffId: payment.staff,
        staffName: getSalaryStaffName(payment),
        department: staffById[String(payment.staff)]?.department_name || '',
        amount: parseFloat(payment.amount || 0),
        status: payment.status,
        note: payment.reason || payment.payment_type_label || 'Deduction',
      }))
      .filter(row => matchesSalarySearch([row.staffName, row.department, row.amount, row.status, row.note]));

    return [...paidRows, ...bonusRows, ...giftRows, ...advanceRows, ...deductionRows]
      .sort((a, b) => a.date.localeCompare(b.date) || a.staffName.localeCompare(b.staffName));
  }, [dateRange, salaryPayments, staffById, searchTerm]);

  const salaryCycleMonthReference = getSalaryCycleMonthReference(dateRange.start, dateRange.end);
  const effectiveSalaryAsOfDate = salaryCycleMonthReference
    ? currentBusinessDate
    : (dateRange.end && dateRange.end < currentBusinessDate
      ? dateRange.end
      : currentBusinessDate);

  useEffect(() => {
    let cancelled = false;
    const fetchSalaryOverview = async () => {
      if (expenseTab !== 'salary' || !dateRange.start || !dateRange.end) return;
      setSalaryOverviewLoading(true);
      try {
        const res = await api.get(
          '/staff-payroll/overview/',
          buildSalaryOverviewParams(
            dateRange.start,
            dateRange.end,
            effectiveSalaryAsOfDate,
            salaryCycleMonthReference ? { cycle_month: salaryCycleMonthReference } : {},
          ),
        );
        if (!cancelled) {
          setSalaryOverview(res.data || { summary: {}, staff_summary: [], event_rows: [] });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(formatApiError(error, 'Failed to load salary overview'));
          setSalaryOverview({ summary: {}, staff_summary: [], event_rows: [] });
        }
      } finally {
        if (!cancelled) {
          setSalaryOverviewLoading(false);
        }
      }
    };
    fetchSalaryOverview();
    return () => { cancelled = true; };
  }, [expenseTab, dateRange.start, dateRange.end, effectiveSalaryAsOfDate, salaryCycleMonthReference, salaryPayments]);

  const salaryOverviewEventRows = useMemo(() => salaryOverview?.event_rows || [], [salaryOverview]);
  const salaryOverviewRows = useMemo(
    () => alignSalarySummaryRowsWithEvents(salaryOverview?.staff_summary || [], salaryOverviewEventRows),
    [salaryOverview, salaryOverviewEventRows]
  );
  const salaryTargetDate = formData.date || currentBusinessDate;
  const salaryStaffOptions = useMemo(() => buildSalaryPaymentStaffOptions(
    buildStaffDropdownOptions(staff, {
      onDate: salaryTargetDate,
      keepValue: formData.staff_id,
    }),
    salaryOverviewRows,
    formData.staff_id,
  ), [staff, salaryTargetDate, formData.staff_id, salaryOverviewRows]);

  const salaryRows = useMemo(() => {
    if (expenseTab !== 'salary') return [];
    return salaryOverviewEventRows
      .filter(row => !salaryStaffFilter || String(row.staffId || row.staff || row.staff_id) === String(salaryStaffFilter))
      .filter(row => matchesSalarySearch([row.staffName, row.department, row.amount, row.status, row.note]))
      .sort((a, b) => a.date.localeCompare(b.date) || a.staffName.localeCompare(b.staffName));
  }, [expenseTab, salaryOverviewEventRows, salaryStaffFilter, searchTerm]);

  const salaryAggregateRows = useMemo(() => {
    if (expenseTab !== 'salary') return [];
    return filterSalarySummaryRows(salaryOverviewRows, { search: searchTerm })
      .filter(row => !salaryStaffFilter || String(row.staffId || row.staff || row.staff_id) === String(salaryStaffFilter));
  }, [expenseTab, salaryOverviewRows, salaryStaffFilter, searchTerm]);

  const salaryFilterStaffOptions = useMemo(
    () => buildSalaryStaffOptions(salaryOverviewRows, salaryOverviewEventRows),
    [salaryOverviewRows, salaryOverviewEventRows]);

  useEffect(() => {
    if (expenseTab !== 'salary' || !salaryStaffFilter) return;
    if (!salaryFilterStaffOptions.some((option) => String(option.value) === String(salaryStaffFilter))) {
      setSalaryStaffFilter('');
    }
  }, [expenseTab, salaryFilterStaffOptions, salaryStaffFilter]);

  const salaryAggregateGrandTotal = useMemo(
    () => buildSalaryGrandTotal(salaryAggregateRows),
    [salaryAggregateRows]
  );

  const salaryAggregateIdsKey = useMemo(
    () => salaryAggregateRows.map((row) => String(row.staffId)).join('|'),
    [salaryAggregateRows]
  );

  useEffect(() => {
    if (expenseTab !== 'salary') return;
    setCollapsedSalarySummaryStaff(new Set(salaryAggregateRows.map((row) => row.staffId)));
  }, [expenseTab, salaryAggregateIdsKey, dateRange.start, dateRange.end, searchTerm]);

  const toggleSalarySummaryStaff = (staffId) => {
    setCollapsedSalarySummaryStaff((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  };

  const allTabSalaryExpenseRows = useMemo(() => {
    if (expenseTab !== 'all' || globalSearchQuery) return [];
    if (filterCategory && filterCategory !== 'salary') return [];
    if (filterPaymentMethod && filterPaymentMethod !== 'cash') return [];
    return salaryPaymentRows
      .filter(row => ['paid', 'advance', 'bonus', 'gift'].includes(row.type))
      .map(row => ({
        id: row.id,
        source_type: 'salary_payment',
        salary_row: row,
        date: row.date,
        category: 'salary',
        amount: row.amount,
        description: row.staffName,
        name: row.type === 'bonus' ? 'Bonus' : row.type === 'gift' ? 'Gift' : row.type === 'advance' ? 'Advance' : 'Salary',
        expense_number: `SAL-${String(row.id).replace(/^(paid|advance|bonus|gift|deduction)-/, '')}`,
        department_name: row.department || '',
        payment_method: 'cash',
        payment_method_display: 'Cash',
        invoice_image: '',
        staff_name: row.staffName,
        salary_type: row.type,
        note: row.note || '',
        status: row.status || '',
      }))
      .sort((a, b) => b.date.localeCompare(a.date) || String(b.expense_number).localeCompare(String(a.expense_number)));
  }, [expenseTab, globalSearchQuery, filterCategory, filterPaymentMethod, salaryPaymentRows]);

  const displayExpenseRows = useMemo(() => {
    if (expenseTab !== 'all') return filteredExpenses;
    return [...filteredExpenses, ...allTabSalaryExpenseRows]
      .sort((a, b) => b.date.localeCompare(a.date) || String(b.expense_number || '').localeCompare(String(a.expense_number || '')));
  }, [expenseTab, filteredExpenses, allTabSalaryExpenseRows]);

  const groupedExpenses = useMemo(() => {
    const groups = {};
    displayExpenseRows.forEach(expense => {
      const date = expense.date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(expense);
    });
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  }, [displayExpenseRows]);

  const displaySummary = useMemo(() => {
    const paidSalaryRows = salaryPaymentRows.filter(row => ['paid', 'advance', 'bonus', 'gift'].includes(row.type));
    const paidSalaryTotal = paidSalaryRows.reduce((sum, row) => sum + row.amount, 0);

    // If global search is active, calculate summary from search results
    if (globalSearchQuery) {
      const searchExpensesByTab = {
        purchases: globalSearchResults.purchases.map(e => ({ ...e, category: 'raw_material' })),
        maintenance: globalSearchResults.maintenance.map(e => ({ ...e, category: 'maintenance' })),
        bills: globalSearchResults.bills.map(e => ({ ...e, category: 'bills' })),
      };
      const searchExpenses = expenseTab === 'all'
        ? [...searchExpensesByTab.purchases, ...searchExpensesByTab.maintenance, ...searchExpensesByTab.bills]
        : (searchExpensesByTab[expenseTab] || []);
      const searchSalaryRows = globalSearchResults.salary
        .filter(payment => ['salary', 'advance', 'bonus', 'gift'].includes(payment.payment_type))
        .map(payment => ({ ...payment, amountNumber: parseFloat(payment.amount || 0) }));
      const includeSalary = expenseTab === 'all' || expenseTab === 'salary';
      const searchSalaryTotal = includeSalary ? searchSalaryRows.reduce((sum, row) => sum + row.amountNumber, 0) : 0;
      const searchTotal = searchExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      const searchCash = searchExpenses.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      const searchDigital = searchExpenses.filter(e => e.payment_method !== 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

      if (expenseTab === 'salary') {
        return {
          total: searchSalaryTotal,
          cash: 0,
          digital: searchSalaryRows.length,
          entries: 0,
        };
      }

      return {
        total: searchTotal + searchSalaryTotal,
        cash: searchCash + searchSalaryTotal,
        digital: searchDigital,
        entries: searchExpenses.length + (includeSalary ? searchSalaryRows.length : 0),
      };
    }

    if (expenseTab === 'salary') {
      const dueRows = salaryRows.filter(row => row.type === 'due');
      return {
        total: paidSalaryTotal,
        cash: dueRows.reduce((sum, row) => sum + row.amount, 0),
        digital: paidSalaryRows.length,
        entries: dueRows.length,
      };
    }

    if (expenseTab === 'all') {
      const expenseTotal = displayExpenseRows.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      return {
        total: expenseTotal,
        cash: displayExpenseRows.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
        digital: displayExpenseRows.filter(e => e.payment_method && e.payment_method !== 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
        entries: displayExpenseRows.length,
      };
    }

    return {
      total: filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
      cash: filteredExpenses.filter(e => e.payment_method === 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
      digital: filteredExpenses.filter(e => e.payment_method !== 'cash').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
      entries: filteredExpenses.length,
    };
  }, [expenseTab, filteredExpenses, salaryRows, salaryPaymentRows, filterCategory, filterPaymentMethod, globalSearchQuery, globalSearchResults, displayExpenseRows]);

  const allExpenseBreakdown = useMemo(() => {
    const sections = {
      purchases: { id: 'purchases', label: 'Inventory', total: 0, items: {}, groups: {} },
      maintenance: { id: 'maintenance', label: 'Maintenance', total: 0, items: {}, groups: {} },
      bills: { id: 'bills', label: 'Bills', total: 0, items: {}, groups: {} },
      salary: { id: 'salary', label: 'Salary', total: 0, items: {}, groups: {} },
      other: { id: 'other', label: 'Other', total: 0, items: {}, groups: {} },
    };

    const addItem = (sectionId, label, amount) => {
      if (!amount) return;
      const section = sections[sectionId];
      section.items[label] = (section.items[label] || 0) + amount;
      section.total += amount;
    };

    const addGroupedItem = (sectionId, groupId, groupLabel, label, amount, includeInSectionTotal = true) => {
      if (!amount) return;
      const section = sections[sectionId];
      if (!section.groups[groupId]) {
        section.groups[groupId] = { id: groupId, label: groupLabel, total: 0, items: {} };
      }
      const group = section.groups[groupId];
      group.items[label] = (group.items[label] || 0) + amount;
      group.total += amount;
      if (includeInSectionTotal) section.total += amount;
    };

    const getKnownStaffDisplayName = (value) => {
      const normalized = String(value || '').trim().toLowerCase();
      if (!normalized) return '';
      const match = staff.find(member => {
        const fullName = staffName(member);
        const candidate = fullName.toLowerCase();
        return candidate && (candidate === normalized || candidate.includes(normalized) || normalized.includes(candidate));
      });
      return match ? staffName(match) : '';
    };

    // Use search results if global search is active
    const expensesToProcess = globalSearchQuery ? [
      ...globalSearchResults.purchases.map(e => ({ ...e, category: 'raw_material' })),
      ...globalSearchResults.maintenance.map(e => ({ ...e, category: 'maintenance' })),
      ...globalSearchResults.bills.map(e => ({ ...e, category: 'bills' }))
    ] : filteredExpenses;

    expensesToProcess.forEach(expense => {
      const amount = parseFloat(expense.amount || 0);
      if (!amount) return;

      if (expense.category === 'raw_material') {
        addItem('purchases', 'Inventory', amount);
        return;
      }

      if (expense.category === 'bills') {
        addItem('bills', getExpenseDisplayName(expense), amount);
        return;
      }

      if (expense.category === 'maintenance') {
        addItem('maintenance', getExpenseDisplayName(expense), amount);
        return;
      }

      if (expense.category === 'salary') {
        const staffDisplayName = getKnownStaffDisplayName(getExpenseDisplayName(expense));
        if (staffDisplayName) addItem('salary', staffDisplayName, amount);
        else addItem('other', 'Unassigned Salary', amount);
        return;
      }

      addItem('other', getExpenseCategoryLabel(expense), amount);
    });

    const salaryRowsForBreakdown = globalSearchQuery
      ? globalSearchResults.salary.map(payment => ({
          type: payment.payment_type === 'salary' ? 'paid' : payment.payment_type,
          staffName: getSalaryStaffName(payment),
          amount: parseFloat(payment.amount || 0),
        }))
      : ((!filterPaymentMethod && (!filterCategory || filterCategory === 'salary')) ? salaryPaymentRows : []);
    salaryRowsForBreakdown.forEach(row => {
      const label = row.staffName || 'Unassigned Staff';
      if (row.type === 'paid' || row.type === 'advance') addItem('salary', label, row.amount);
      else if (row.type === 'bonus') addGroupedItem('salary', 'bonus', 'Bonus', label, row.amount);
      else if (row.type === 'gift') addGroupedItem('salary', 'gift', 'Gift', label, row.amount);
      else if (row.type === 'deduction') addGroupedItem('salary', 'deductions', 'Deductions', label, row.amount, false);
    });

    return Object.values(sections)
      .filter(section => section.id !== 'other' || section.total > 0)
      .map(section => ({
        ...section,
        items: Object.entries(section.items)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([label, amount]) => ({ label, amount })),
        groups: Object.values(section.groups)
          .filter(group => group.total > 0)
          .map(group => ({
            ...group,
            items: Object.entries(group.items)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([label, amount]) => ({ label, amount })),
          })),
      }));
  }, [filteredExpenses, salaryPaymentRows, inventoryItems, staff, filterCategory, filterPaymentMethod, globalSearchQuery, globalSearchResults]);
  const allExpenseBreakdownIdsKey = useMemo(
    () => allExpenseBreakdown.map((section) => section.id).join('|'),
    [allExpenseBreakdown]
  );

  useEffect(() => {
    if (expenseTab !== 'all') return;
    setCollapsedExpenseBreakdown(new Set(allExpenseBreakdown.map((section) => section.id)));
  }, [expenseTab, allExpenseBreakdownIdsKey]);

  const expenseCalendarData = useMemo(() => {
    const data = {};
    // Use search results if global search is active
    const expensesToProcess = globalSearchQuery ? [
      ...globalSearchResults.purchases.map(e => ({ ...e, category: 'raw_material' })),
      ...globalSearchResults.maintenance.map(e => ({ ...e, category: 'maintenance' })),
      ...globalSearchResults.bills.map(e => ({ ...e, category: 'bills' }))
    ] : displayExpenseRows;

    expensesToProcess.forEach(expense => {
      const ds = expense.date;
      if (!data[ds]) data[ds] = { count: 0, total: 0, names: [] };
      data[ds].count += 1;
      data[ds].total += parseFloat(expense.amount || 0);
      data[ds].names.push(expense.source_type === 'salary_payment' ? (expense.staff_name || expense.description || 'Salary') : getExpenseDisplayName(expense));
    });
    return data;
  }, [displayExpenseRows, globalSearchQuery, globalSearchResults]);

  const salaryCalendarData = useMemo(() => {
    if (expenseTab !== 'salary') return {};
    return buildSalaryCalendarData(salaryRows);
  }, [expenseTab, salaryRows]);

  const changeCalendarMonth = (delta) => {
    const d = new Date(calendarMonth.year, calendarMonth.month + delta, 1);
    const first = formatDateValue(new Date(d.getFullYear(), d.getMonth(), 1));
    const last = formatDateValue(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    setDateRange({ start: first, end: last });
    setDateRangePreset('this_month');
    setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
  };

  const applyExpenseCalendarPeriod = ({ mode, startDate, endDate, year, month }) => {
    const startHour = String(businessStartHour).padStart(2, '0');
    const endHour = String((businessStartHour - 1 + 24) % 24).padStart(2, '0');
    setDateRangePreset(mode === 'year' ? 'this_year' : 'this_month');
    setDateRange({ start: startDate, end: endDate });
    setCalendarMonth({ year, month: mode === 'year' ? 0 : month });
    setFromTime(`${startHour}:00`);
    setToTime(`${endHour}:59`);
  };

  useEffect(() => {
    if (viewMode !== 'calendar' || ['this_month', 'this_year'].includes(dateRangePreset)) return;
    const base = dateRange.start ? new Date(`${dateRange.start}T12:00:00`) : new Date();
    if (Number.isNaN(base.getTime())) return;
    const first = formatDateValue(new Date(base.getFullYear(), base.getMonth(), 1));
    const last = formatDateValue(new Date(base.getFullYear(), base.getMonth() + 1, 0));
    setDateRange({ start: first, end: last });
    setDateRangePreset('this_month');
    setCalendarMonth({ year: base.getFullYear(), month: base.getMonth() });
  }, [viewMode, dateRangePreset, dateRange.start]);

  function getExpenseCategoryLabel(expenseOrCategory) {
    if (typeof expenseOrCategory === 'object' && expenseOrCategory !== null) {
      return expenseOrCategory.category_display || formatCategoryLabel(expenseOrCategory.category);
    }
    const match = expenseCategories.find(category => category.value === expenseOrCategory);
    return match?.label || formatCategoryLabel(expenseOrCategory);
  }

  // Parse a purchase description like "ItemName - Qty 5 kg" → { name, qtyText }
  function getExpenseDisplayName(expense) {
    const explicitName = String(expense?.name || '').trim();
    if (explicitName) return explicitName;
    const description = String(expense?.description || '').trim();
    if (description) return description;
    return getExpenseCategoryLabel(expense);
  }

  function getNameBasedPaymentLabel(expense) {
    if (expense?.payment_method === 'cash') return 'Cash';
    if (['digital', 'card', 'bank'].includes(expense?.payment_method)) return 'Digital/Bank';
    return expense?.payment_method_display || expense?.payment_method || '-';
  }

  function parsePurchaseDesc(desc) {
    if (!desc) return { name: '', qtyText: '' };
    const m = desc.match(/^(.+?)\s*-\s*Qty[:\s]+(.+?)(?:\s*\(Unit:[^)]*\))?$/i);
    if (m) return { name: m[1].trim(), qtyText: m[2].trim() };
    return { name: desc, qtyText: '' };
  }

  function getPrimaryInventoryLine(expense) {
    if (!expense || expense.category !== 'raw_material' || !Array.isArray(expense.line_items)) return null;
    return expense.line_items.find(line => line.raw_material || line.raw_material_name || line.item_name) || null;
  }

  // Determine if an expense is linked to a known inventory item
  function isInventoryItemExpense(expense) {
    return !!getInventoryMatch(expense);
  }

  // Resolve matching inventory record (uses canonical inventory name, not smart-paste name)
  function getInventoryMatch(expense) {
    if (!expense || expense.category !== 'raw_material') return null;
    const li = getPrimaryInventoryLine(expense);
    if (li) {
      const matchById = li.raw_material ? inventoryItems.find(inv => String(inv.id) === String(li.raw_material)) : null;
      if (matchById) return { ...matchById, name: matchById.name, unit: getInventoryUnitAbbr(matchById) };
      const linkedName = li.raw_material_name || li.item_name || '';
      const matchByName = linkedName
        ? inventoryItems.find(inv => inv.name && inv.name.toLowerCase() === linkedName.toLowerCase())
        : null;
      if (matchByName) return { ...matchByName, name: matchByName.name, unit: getInventoryUnitAbbr(matchByName) };
      return { id: li.raw_material || '', name: linkedName, unit: li.unit_abbr || li.unit || '' };
    }
    const { name, qtyText } = parsePurchaseDesc(expense.description);
    if (!name) return null;
    const lc = name.toLowerCase();
    const match = inventoryItems.find(inv => inv.name && inv.name.toLowerCase() === lc);
    if (match) return { ...match, name: match.name, unit: getInventoryUnitAbbr(match) };
    return { name, unit: qtyText || '' };
  }

  function getInventoryExpenseDisplay(expense) {
    if (!expense || expense.category !== 'raw_material') {
      return { itemName: expense?.description || '-', qtyText: '' };
    }
    const inventoryMatch = getInventoryMatch(expense);
    const primaryLine = getPrimaryInventoryLine(expense);
    const parsed = parsePurchaseDesc(expense.description);
    const itemName = inventoryMatch?.name || primaryLine?.raw_material_name || primaryLine?.item_name || parsed.name || expense.description || '-';
    const unit = inventoryMatch?.unit || primaryLine?.unit_abbr || primaryLine?.unit || '';
    const quantity = primaryLine?.quantity;
    const qtyText = quantity
      ? `${quantity}${unit ? ` ${unit}` : ''}`.trim()
      : (parsed.qtyText || unit || '');
    return { itemName, qtyText };
  }

  function getExpenseDepartmentDisplay(expense) {
    if (!expense) return '';
    if (expense.source_type === 'salary_payment') return expense.department_name || 'Staff-wide';
    if (expense.category === 'raw_material') {
      const inventoryMatch = getInventoryMatch(expense);
      return inventoryMatch?.department_name || expense.department_name || 'Storewide';
    }
    return expense.department_name || 'Store-wide';
  }

  function matchesPurchaseFilters(expense) {
    if (!filterPurchaseDepartment && !filterPurchaseSupplier && !filterPurchaseItem) return true;
    const inventoryMatch = getInventoryMatch(expense);
    const itemId = inventoryMatch?.id || '';
    const departmentId = inventoryMatch?.department || expense.department || '';
    const supplierId = inventoryMatch?.supplier || '';
    if (filterPurchaseItem && String(itemId) !== String(filterPurchaseItem)) return false;
    if (filterPurchaseDepartment && String(departmentId) !== String(filterPurchaseDepartment)) return false;
    if (filterPurchaseSupplier && String(supplierId) !== String(filterPurchaseSupplier)) return false;
    return true;
  }

  // Payment method color class (text only, no background)
  function getPaymentMethodColor(pm) {
    if (pm === 'cash') return 'text-green-600';
    if (pm === 'card') return 'text-red-600';
    return 'text-blue-600'; // digital/bank and default
  }

  const handleGlobalSearch = async (query) => {
    setGlobalSearchQuery(query);
    if (!query.trim()) {
      setGlobalSearchResults(EMPTY_GLOBAL_SEARCH_RESULTS);
      setGlobalSearchPagination(EMPTY_GLOBAL_SEARCH_PAGINATION);
      return;
    }

    const buildExpenseSearchParams = (category, page = 1) => {
      const params = new URLSearchParams({
        category,
        search: query,
        page: String(page),
        page_size: String(GLOBAL_SEARCH_PAGE_SIZE),
      });
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (filterPaymentMethod) params.append('payment_method', filterPaymentMethod);
      return params.toString();
    };
    const buildSalarySearchParams = (page = 1) => {
      const params = new URLSearchParams({
        search: query,
        page: String(page),
        page_size: String(GLOBAL_SEARCH_PAGE_SIZE),
      });
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      return params.toString();
    };

    setGlobalSearchLoading(true);
    try {
      const [purchasesRes, maintenanceRes, billsRes, salaryRes] = await Promise.all([
        api.get(`/expenses/?${buildExpenseSearchParams('raw_material')}`),
        api.get(`/expenses/?${buildExpenseSearchParams('maintenance')}`),
        api.get(`/expenses/?${buildExpenseSearchParams('bills')}`),
        api.get(`/staff-advances/?${buildSalarySearchParams()}`),
      ]);

      setGlobalSearchResults({
        purchases: purchasesRes.data.results || purchasesRes.data || [],
        maintenance: maintenanceRes.data.results || maintenanceRes.data || [],
        bills: billsRes.data.results || billsRes.data || [],
        salary: salaryRes.data.results || salaryRes.data || [],
      });
      setGlobalSearchPagination({
        purchases: { page: 1, total: purchasesRes.data.count || 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
        maintenance: { page: 1, total: maintenanceRes.data.count || 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
        bills: { page: 1, total: billsRes.data.count || 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
        salary: { page: 1, total: salaryRes.data.count || 0, pageSize: GLOBAL_SEARCH_PAGE_SIZE },
      });
    } catch (error) {
      console.error('Global search error:', error);
      setGlobalSearchResults(EMPTY_GLOBAL_SEARCH_RESULTS);
    } finally {
      setGlobalSearchLoading(false);
    }
  };

  const handleGlobalSearchPageChange = async (group, page) => {
    const buildSearchParams = (category = '') => {
      const params = new URLSearchParams({
        search: globalSearchQuery,
        page: String(page),
        page_size: String(GLOBAL_SEARCH_PAGE_SIZE),
      });
      if (category) params.append('category', category);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (category && filterPaymentMethod) params.append('payment_method', filterPaymentMethod);
      return params.toString();
    };

    setGlobalSearchLoading(true);
    try {
      let res;
      if (group === 'purchases') {
        res = await api.get(`/expenses/?${buildSearchParams('raw_material')}`);
        setGlobalSearchResults(prev => ({ ...prev, purchases: res.data.results || res.data || [] }));
        setGlobalSearchPagination(prev => ({ ...prev, purchases: { ...prev.purchases, page, total: res.data.count || 0 } }));
      } else if (group === 'maintenance') {
        res = await api.get(`/expenses/?${buildSearchParams('maintenance')}`);
        setGlobalSearchResults(prev => ({ ...prev, maintenance: res.data.results || res.data || [] }));
        setGlobalSearchPagination(prev => ({ ...prev, maintenance: { ...prev.maintenance, page, total: res.data.count || 0 } }));
      } else if (group === 'bills') {
        res = await api.get(`/expenses/?${buildSearchParams('bills')}`);
        setGlobalSearchResults(prev => ({ ...prev, bills: res.data.results || res.data || [] }));
        setGlobalSearchPagination(prev => ({ ...prev, bills: { ...prev.bills, page, total: res.data.count || 0 } }));
      } else if (group === 'salary') {
        res = await api.get(`/staff-advances/?${buildSearchParams()}`);
        setGlobalSearchResults(prev => ({ ...prev, salary: res.data.results || res.data || [] }));
        setGlobalSearchPagination(prev => ({ ...prev, salary: { ...prev.salary, page, total: res.data.count || 0 } }));
      }
    } catch (error) {
      console.error('Pagination error:', error);
    } finally {
      setGlobalSearchLoading(false);
    }
  };

  const handleGlobalSearchItemClick = (item, tab) => {
    setExpenseTab(tab);
    // Don't clear search results - keep them visible after closing modal

    if (tab === 'salary') {
      // For salary, we might need to open the edit modal or navigate to the specific entry
      if (item.id) {
        openSalaryEditModal({ id: `paid-${item.id}`, type: 'paid', date: item.advance_date, amount: item.amount, staffName: item.staff_name || item.staff_member_name });
      }
    } else {
      // For expenses, open the edit modal
      if (item.id) {
        openEditModal(item);
      }
    }
  };

  const toggleGlobalSearchGroup = (group) => {
    setCollapsedGlobalSearchGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  function formatExpenseDateHeader(ds) {
    return formatDateWithWeekday(ds);
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Expense Management</h1>
        <p className="text-sm sm:text-base text-gray-500">Track and manage all business expenses</p>
      </div>

      {/* View Mode */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <UiViewToggle
          options={[{ value: 'table', label: 'Table' }, { value: 'calendar', label: 'Calendar' }]}
          value={viewMode}
          onChange={(v) => {
            setViewMode(v);
            if (v === 'calendar' && dateRange.start) {
              const d = new Date(`${dateRange.start}T12:00:00`);
              const first = formatDateValue(new Date(d.getFullYear(), d.getMonth(), 1));
              const last = formatDateValue(new Date(d.getFullYear(), d.getMonth() + 1, 0));
              setDateRange({ start: first, end: last });
              setDateRangePreset('this_month');
            }
          }}
        />
      </div>

      {/* Date Filter */}
      <UiDateRangeBlock
        preset={dateRangePreset}
        presetOptions={viewMode === 'calendar'
          ? buildStandardPresetOptions(['this_month', 'this_year'])
          : buildStandardPresetOptions(['today', 'this_week', 'this_month', 'this_year', 'custom'])}
        onPresetChange={(preset) => {
          if (viewMode === 'calendar') {
            const base = new Date(calendarMonth.year, calendarMonth.month, 1);
            const periodStart = preset === 'this_year'
              ? new Date(base.getFullYear(), 0, 1)
              : new Date(base.getFullYear(), base.getMonth(), 1);
            const periodEnd = preset === 'this_year'
              ? new Date(base.getFullYear(), 11, 31)
              : new Date(base.getFullYear(), base.getMonth() + 1, 0);
            applyExpenseCalendarPeriod({
              mode: preset === 'this_year' ? 'year' : 'month',
              startDate: formatDateValue(periodStart),
              endDate: formatDateValue(periodEnd),
              year: periodStart.getFullYear(),
              month: periodStart.getMonth(),
            });
            return;
          }
          setDateRangePreset(preset);
          const today = new Date();
          let start;
          let end;
          const startHour = String(businessStartHour).padStart(2, '0');
          const endHour = String((businessStartHour - 1 + 24) % 24).padStart(2, '0');

          if (preset === 'today') {
            start = formatDateValue(today);
            end = formatDateValue(today);
          } else if (preset === 'this_week') {
            const dayOfWeek = (today.getDay() + 6) % 7;
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - dayOfWeek);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            start = formatDateValue(startOfWeek);
            end = formatDateValue(endOfWeek);
          } else if (preset === 'this_month') {
            start = formatDateValue(new Date(today.getFullYear(), today.getMonth(), 1));
            end = formatDateValue(new Date(today.getFullYear(), today.getMonth() + 1, 0));
          } else if (preset === 'this_year') {
            start = formatDateValue(new Date(today.getFullYear(), 0, 1));
            end = formatDateValue(new Date(today.getFullYear(), 11, 31));
          } else {
            return;
          }

          setFromTime(`${startHour}:00`);
          setToTime(`${endHour}:59`);
          setDateRange({ start, end });
        }}
        onPrevious={() => shiftExpenseRange(-1)}
        onNext={() => shiftExpenseRange(1)}
        periodPicker={viewMode === 'calendar'}
        onPeriodChange={applyExpenseCalendarPeriod}
        fromDate={dateRange.start}
        onFromDateChange={(nextDate) => {
          const range = resolveStandardStartEdit({
            preset: dateRangePreset,
            nextStartDate: nextDate,
            startTime: fromTime,
            endTime: toTime,
            businessDayStartHour: businessStartHour,
          });
          if (range) {
            setDateRange({ start: range.startDate, end: range.endDate });
            return;
          }
          setDateRange(prev => ({ ...prev, start: nextDate }));
          setDateRangePreset('custom');
        }}
        toDate={filterEndCalendarDate}
        onToDateChange={(nextDate) => {
          const nextEndDate = addDays(nextDate, -1);
          const keepPreset = shouldKeepStandardPresetForEnd({
            preset: dateRangePreset,
            startDate: dateRange.start,
            nextEndDate,
            startTime: fromTime,
            endTime: toTime,
            businessDayStartHour: businessStartHour,
          });
          setDateRange(prev => ({ ...prev, end: nextEndDate }));
          if (!keepPreset) setDateRangePreset('custom');
        }}
        fromTime={fromTime}
        onFromTimeChange={(nextTime) => {
          setFromTime(nextTime);
          const boundary = getBusinessBoundaryTimes(businessStartHour);
          if (dateRangePreset !== 'custom' && (nextTime !== boundary.startTime || toTime !== boundary.endTime)) {
            setDateRangePreset('custom');
          }
        }}
        toTime={toTime}
        onToTimeChange={(nextTime) => {
          setToTime(nextTime);
          const boundary = getBusinessBoundaryTimes(businessStartHour);
          if (dateRangePreset !== 'custom' && (fromTime !== boundary.startTime || nextTime !== boundary.endTime)) {
            setDateRangePreset('custom');
          }
        }}
        showTime
        tone="red"
        rangeText={`Selected range: ${dateRange.start && filterEndCalendarDate ? formatUiDateTimeRange(dateRange.start, fromTime, filterEndCalendarDate, toTime) : '-'}`}
      />

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search inventory, maintenance, bills, salary..."
            value={globalSearchQuery}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleGlobalSearch(e.target.value);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
          />
          {globalSearchQuery && (
            <button
              onClick={() => { setSearchTerm(''); setGlobalSearchQuery(''); setGlobalSearchResults(EMPTY_GLOBAL_SEARCH_RESULTS); setGlobalSearchPagination(EMPTY_GLOBAL_SEARCH_PAGINATION); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Collapsible Global Search Results */}
        {globalSearchQuery && !globalSearchLoading && (
          <div className="mt-3 space-y-2">
            {globalSearchResults.purchases.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleGlobalSearchGroup('purchases')}
                  className="w-full px-3 py-2 bg-orange-50 hover:bg-orange-100 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-orange-700 text-sm">Inventory ({globalSearchPagination.purchases.total} total)</span>
                  {collapsedGlobalSearchGroups.purchases ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                {!collapsedGlobalSearchGroups.purchases && (
                  <>
                    <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                      {globalSearchResults.purchases.map(item => (
                        <button
                          key={item.id}
                          onClick={() => handleGlobalSearchItemClick(item, 'purchases')}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{getExpenseDisplayName(item)}</span>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{formatMoneyDetailed(item.amount)}</span>
                        </button>
                      ))}
                    </div>
                    {globalSearchPagination.purchases.total > globalSearchPagination.purchases.pageSize && (
                      <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 overflow-x-auto">
                        <div className="flex items-center gap-1 min-w-max">
                          <button
                            onClick={() => handleGlobalSearchPageChange('purchases', globalSearchPagination.purchases.page - 1)}
                            disabled={globalSearchPagination.purchases.page === 1}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Prev
                          </button>
                          {Array.from({ length: Math.min(5, Math.ceil(globalSearchPagination.purchases.total / globalSearchPagination.purchases.pageSize)) }, (_, i) => {
                            const pageNum = i + 1;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handleGlobalSearchPageChange('purchases', pageNum)}
                                className={`px-2 py-1 text-xs border rounded ${globalSearchPagination.purchases.page === pageNum ? 'bg-orange-600 text-white' : 'hover:bg-gray-100'}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handleGlobalSearchPageChange('purchases', globalSearchPagination.purchases.page + 1)}
                            disabled={globalSearchPagination.purchases.page >= Math.ceil(globalSearchPagination.purchases.total / globalSearchPagination.purchases.pageSize)}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                          <span className="text-xs text-gray-500 ml-2">
                            Page {globalSearchPagination.purchases.page} of {Math.ceil(globalSearchPagination.purchases.total / globalSearchPagination.purchases.pageSize)}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {globalSearchResults.maintenance.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleGlobalSearchGroup('maintenance')}
                  className="w-full px-3 py-2 bg-blue-50 hover:bg-blue-100 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-blue-700 text-sm">Maintenance ({globalSearchPagination.maintenance.total} total)</span>
                  {collapsedGlobalSearchGroups.maintenance ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                {!collapsedGlobalSearchGroups.maintenance && (
                  <>
                    <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                      {globalSearchResults.maintenance.map(item => (
                        <button
                          key={item.id}
                          onClick={() => handleGlobalSearchItemClick(item, 'maintenance')}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{getExpenseDisplayName(item)}</span>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{formatMoneyDetailed(item.amount)}</span>
                        </button>
                      ))}
                    </div>
                    {globalSearchPagination.maintenance.total > globalSearchPagination.maintenance.pageSize && (
                      <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 overflow-x-auto">
                        <div className="flex items-center gap-1 min-w-max">
                          <button
                            onClick={() => handleGlobalSearchPageChange('maintenance', globalSearchPagination.maintenance.page - 1)}
                            disabled={globalSearchPagination.maintenance.page === 1}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Prev
                          </button>
                          {Array.from({ length: Math.min(5, Math.ceil(globalSearchPagination.maintenance.total / globalSearchPagination.maintenance.pageSize)) }, (_, i) => {
                            const pageNum = i + 1;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handleGlobalSearchPageChange('maintenance', pageNum)}
                                className={`px-2 py-1 text-xs border rounded ${globalSearchPagination.maintenance.page === pageNum ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handleGlobalSearchPageChange('maintenance', globalSearchPagination.maintenance.page + 1)}
                            disabled={globalSearchPagination.maintenance.page >= Math.ceil(globalSearchPagination.maintenance.total / globalSearchPagination.maintenance.pageSize)}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                          <span className="text-xs text-gray-500 ml-2">
                            Page {globalSearchPagination.maintenance.page} of {Math.ceil(globalSearchPagination.maintenance.total / globalSearchPagination.maintenance.pageSize)}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {globalSearchResults.bills.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleGlobalSearchGroup('bills')}
                  className="w-full px-3 py-2 bg-purple-50 hover:bg-purple-100 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-purple-700 text-sm">Bills ({globalSearchPagination.bills.total} total)</span>
                  {collapsedGlobalSearchGroups.bills ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                {!collapsedGlobalSearchGroups.bills && (
                  <>
                    <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                      {globalSearchResults.bills.map(item => (
                        <button
                          key={item.id}
                          onClick={() => handleGlobalSearchItemClick(item, 'bills')}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{getExpenseDisplayName(item)}</span>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{formatMoneyDetailed(item.amount)}</span>
                        </button>
                      ))}
                    </div>
                    {globalSearchPagination.bills.total > globalSearchPagination.bills.pageSize && (
                      <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 overflow-x-auto">
                        <div className="flex items-center gap-1 min-w-max">
                          <button
                            onClick={() => handleGlobalSearchPageChange('bills', globalSearchPagination.bills.page - 1)}
                            disabled={globalSearchPagination.bills.page === 1}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Prev
                          </button>
                          {Array.from({ length: Math.min(5, Math.ceil(globalSearchPagination.bills.total / globalSearchPagination.bills.pageSize)) }, (_, i) => {
                            const pageNum = i + 1;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handleGlobalSearchPageChange('bills', pageNum)}
                                className={`px-2 py-1 text-xs border rounded ${globalSearchPagination.bills.page === pageNum ? 'bg-purple-600 text-white' : 'hover:bg-gray-100'}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handleGlobalSearchPageChange('bills', globalSearchPagination.bills.page + 1)}
                            disabled={globalSearchPagination.bills.page >= Math.ceil(globalSearchPagination.bills.total / globalSearchPagination.bills.pageSize)}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                          <span className="text-xs text-gray-500 ml-2">
                            Page {globalSearchPagination.bills.page} of {Math.ceil(globalSearchPagination.bills.total / globalSearchPagination.bills.pageSize)}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {globalSearchResults.salary.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleGlobalSearchGroup('salary')}
                  className="w-full px-3 py-2 bg-green-50 hover:bg-green-100 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-green-700 text-sm">Salary ({globalSearchPagination.salary.total} total)</span>
                  {collapsedGlobalSearchGroups.salary ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                {!collapsedGlobalSearchGroups.salary && (
                  <>
                    <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                      {globalSearchResults.salary.map(item => (
                        <button
                          key={item.id}
                          onClick={() => handleGlobalSearchItemClick(item, 'salary')}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm text-gray-700 flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{item.staff_name || item.staff_member_name || 'Unknown'}</span>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{formatMoneyDetailed(item.amount)}</span>
                        </button>
                      ))}
                    </div>
                    {globalSearchPagination.salary.total > globalSearchPagination.salary.pageSize && (
                      <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 overflow-x-auto">
                        <div className="flex items-center gap-1 min-w-max">
                          <button
                            onClick={() => handleGlobalSearchPageChange('salary', globalSearchPagination.salary.page - 1)}
                            disabled={globalSearchPagination.salary.page === 1}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Prev
                          </button>
                          {Array.from({ length: Math.min(5, Math.ceil(globalSearchPagination.salary.total / globalSearchPagination.salary.pageSize)) }, (_, i) => {
                            const pageNum = i + 1;
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handleGlobalSearchPageChange('salary', pageNum)}
                                className={`px-2 py-1 text-xs border rounded ${globalSearchPagination.salary.page === pageNum ? 'bg-green-600 text-white' : 'hover:bg-gray-100'}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handleGlobalSearchPageChange('salary', globalSearchPagination.salary.page + 1)}
                            disabled={globalSearchPagination.salary.page >= Math.ceil(globalSearchPagination.salary.total / globalSearchPagination.salary.pageSize)}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                          <span className="text-xs text-gray-500 ml-2">
                            Page {globalSearchPagination.salary.page} of {Math.ceil(globalSearchPagination.salary.total / globalSearchPagination.salary.pageSize)}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {globalSearchResults.purchases.length === 0 && globalSearchResults.maintenance.length === 0 && globalSearchResults.bills.length === 0 && globalSearchResults.salary.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">No results found</p>
            )}
          </div>
        )}

        {globalSearchLoading && (
          <p className="text-sm text-gray-500 text-center py-2">Searching...</p>
        )}
      </div>

      {/* Expense Tabs */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-2 min-w-max pb-1">
          <UiTabs
            tabs={EXPENSE_TABS}
            active={expenseTab}
            onChange={(id) => {
              setExpenseTab(id);
              setFilterCategory('');
              setFilterPurchaseDepartment('');
              setFilterPurchaseSupplier('');
              setFilterExpenseName('');
              if (id === 'salary') setViewMode('calendar');
            }}
          />
        </div>
      </div>

      {/* Action Buttons */}
      {expenseTab !== 'all' && (
      <div className="flex gap-3">
        <button
          onClick={handleSmartPaste}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700 text-sm"
        >
          Smart Paste
        </button>
        <button
          onClick={() => { 
            setPendingExpenseEntries([]);
            resetForm(); 
            if (expenseTab === 'purchases') {
              fetchInventory();
            }
            setShowModal(true); 
          }}
          className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 text-sm"
        >
          {expenseTab === 'purchases' ? 'Add Inventory' : 'Add Expense'}
        </button>
      </div>
      )}

      {expenseTab === 'salary' && (
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <select
              value={salaryStaffFilter}
              onChange={(e) => setSalaryStaffFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
            >
              <option value="">All Staff</option>
              {salaryFilterStaffOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Other Filters */}
      {expenseTab !== 'salary' && (
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${expenseTab === 'salary' ? 'lg:grid-cols-1' : expenseTab === 'purchases' ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-3 sm:gap-4`}>
          {expenseTab !== 'salary' && (
            isNameBasedTab(expenseTab) ? (
              <select
                value={filterExpenseName}
                onChange={(e) => setFilterExpenseName(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="">All Categories</option>
                {(expenseNameOptions[expenseTab] || []).map(option => (
                  <option key={option.id} value={option.name}>{option.name}</option>
                ))}
              </select>
            ) : expenseTab === 'purchases' ? (
              <>
                <select
                  value={filterPurchaseDepartment}
                  onChange={(e) => setFilterPurchaseDepartment(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">All Departments</option>
                  {departments.map(department => (
                    <option key={department.id} value={department.id}>{department.name}</option>
                  ))}
                </select>
                <select
                  value={filterPurchaseSupplier}
                  onChange={(e) => setFilterPurchaseSupplier(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">All Suppliers</option>
                  {purchaseSupplierOptions.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
                <select
                  value={filterPurchaseItem}
                  onChange={(e) => setFilterPurchaseItem(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">All Items</option>
                  {purchaseItemOptions.map(item => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </select>
              </>
            ) : (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="">All Expenses</option>
                {expenseCategories.map(category => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
            )
          )}

          {expenseTab !== 'salary' && <select
            value={filterPaymentMethod}
            onChange={(e) => setFilterPaymentMethod(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
          >
            <option value="">All Payment Methods</option>
            {PAYMENT_METHODS.map(pm => (
              <option key={pm.value} value={pm.value}>{pm.label}</option>
            ))}
          </select>}
        </div>
      </div>
      )}

      {/* Summary Cards */}
      {expenseTab === 'salary' ? (
        <div className="space-y-2">
          {salaryOverviewLoading && (
            <div className="flex items-center gap-2 text-sm text-primary-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <span className="inline-block w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
              Loading salary data…
            </div>
          )}
          {!salaryOverviewLoading && (
            <>
          <div className="rounded-xl border border-orange-200 bg-orange-50/80 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-orange-700 font-semibold">Grand Total</p>
                <p className="text-sm font-semibold text-gray-800">{salaryAggregateGrandTotal.count} staff</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">As of {dateRange.end ? formatDate(dateRange.end) : '—'}, excluding</p>
                <p className="text-sm font-bold text-orange-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalUnpaidAsOfToday)}</p>
                <p className="text-xs text-gray-500">Combined due total: {formatSalaryCurrency(salaryAggregateGrandTotal.totalPayableOnDueDate)}</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
              <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                <p className="text-xs font-semibold text-gray-700">Over / Underpayment Carried Forward</p>
                <p className={`text-sm font-bold ${salaryAggregateGrandTotal.totalSignedCarryForward >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{formatSignedCarry(salaryAggregateGrandTotal.totalSignedCarryForward, formatSalaryCurrency)}</p>
              </div>
              <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                <p className="text-xs font-semibold text-gray-700">Advance in Selected Cycle</p>
                <p className="text-sm font-bold text-emerald-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalAdvance)}</p>
              </div>
              <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                <p className="text-xs font-semibold text-gray-700">Deductions in Selected Cycle</p>
                <p className="text-sm font-bold text-rose-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalDeductions)}</p>
              </div>
              <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                <p className="text-xs font-semibold text-gray-700">Bonus in Selected Cycle</p>
                <p className="text-sm font-bold text-emerald-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalBonus)}</p>
              </div>
              <div className="rounded-lg border border-white/70 bg-white/70 p-2">
                <p className="text-xs font-semibold text-gray-700">Gift in Selected Cycle</p>
                <p className="text-sm font-bold text-emerald-700">{formatSalaryCurrency(salaryAggregateGrandTotal.totalGift)}</p>
              </div>
            </div>
          </div>

          {salaryAggregateRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-orange-200 p-4 text-center text-sm text-gray-400">No staff salary summary for this selection.</div>
          ) : (
            salaryAggregateRows.map((row) => (
              <div key={`expense-salary-summary-${row.staffId}`} className="border border-gray-100 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSalarySummaryStaff(row.staffId)}
                  className="w-full px-3 py-3 bg-gray-50 hover:bg-gray-100 grid grid-cols-[minmax(0,1fr)_11rem] items-center gap-3 text-left transition"
                >
                    <span className="flex items-center gap-2 min-w-0">
                      {collapsedSalarySummaryStaff.has(row.staffId) ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-800 truncate">{row.staffName}</span>
                        <span className="block text-xs text-gray-500 truncate">{row.department || 'No department'}</span>
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block text-xs text-gray-500">
                        {row.cycleDisplayState === 'current' ? 'Accrued as of' : 'Outstanding as of'} {row.snapshotDate ? formatDate(row.snapshotDate) : '-'}
                      </span>
                      <span className="block text-sm font-bold text-orange-700">{formatSalaryCurrency(row.unpaidAsOfToday)}</span>
                      <span className="block text-[11px] text-gray-500">{row.currentDueDate ? formatDate(row.currentDueDate) : '-'} · {formatSalaryCurrency(row.totalPayableOnDueDate)}</span>
                    </span>
                  </button>
                  {!collapsedSalarySummaryStaff.has(row.staffId) && (
                    <SalaryCycleSummary
                      summary={row}
                      breakdown={row.breakdown || {}}
                      deductionRecords={salaryPayments.filter((record) => (
                        record.payment_type === 'deduction'
                        && String(record.staff?.id ?? record.staff) === String(row.staffId)
                      ))}
                      asOfDate={row.snapshotDate || effectiveSalaryAsOfDate}
                      formatCurrency={formatSalaryCurrency}
                      formatDate={formatDate}
                    />
                )}
              </div>
            ))
          )}
            </>
          )}
        </div>
      ) : (
        <UiSummaryCards>
          <UiSummaryCard tone="text-primary-600"
            label={expenseTab === 'salary' ? 'Salary Paid' : 'Total Expenses'}
            value={formatMoneyCompact(displaySummary.total)} />
          <UiSummaryCard
            label={expenseTab === 'salary' ? 'Salary Due' : 'Cash Expenses'}
            value={formatMoneyCompact(displaySummary.cash)} />
          <UiSummaryCard
            label={expenseTab === 'salary' ? 'Paid Entries' : 'Digital'}
            value={expenseTab === 'salary' ? displaySummary.digital : formatMoneyCompact(displaySummary.digital)} />
          <UiSummaryCard
            label={expenseTab === 'salary' ? 'Due Staff' : 'Entries'}
            value={expenseTab === 'salary' ? displaySummary.entries : displaySummary.entries.toLocaleString()} />
        </UiSummaryCards>
      )}

      {/* Category Breakdown */}
      {expenseTab === 'all' ? (
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3">All Expenses</h3>
        <div className="space-y-2">
          {allExpenseBreakdown.map(section => {
            const isCollapsed = collapsedExpenseBreakdown.has(section.id);
            const hasItems = section.items.length > 0 || section.groups.length > 0;
            return (
              <div key={section.id} className="border border-gray-100 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleExpenseBreakdownSection(section.id)}
                  className="w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 grid grid-cols-[minmax(0,1fr)_8rem] items-center gap-3 text-left transition"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    <span className="text-xs sm:text-sm font-medium text-gray-700">{section.label}</span>
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">{formatMoneyCompact(section.total)}</span>
                </button>
                {!isCollapsed && (
                  <div className="divide-y divide-gray-100">
                    {hasItems ? (
                      <>
                        {section.items.map(item => (
                          <div key={item.label} className="px-9 py-2 grid grid-cols-[minmax(0,1fr)_8rem] items-center gap-3 text-left">
                            <span className="text-xs sm:text-sm text-gray-600">{item.label}</span>
                            <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">{formatMoneyCompact(item.amount)}</span>
                          </div>
                        ))}
                        {section.groups.map(group => (
                          <React.Fragment key={group.id}>
                            <div className="px-9 py-2 bg-gray-50 grid grid-cols-[minmax(0,1fr)_8rem] items-center gap-3 text-left">
                              <span className="text-xs sm:text-sm font-medium text-gray-700">{group.label}</span>
                              <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">{formatMoneyCompact(group.total)}</span>
                            </div>
                            {group.items.map(item => (
                              <div key={`${group.id}-${item.label}`} className="pl-12 pr-9 py-2 grid grid-cols-[minmax(0,1fr)_8rem] items-center gap-3 text-left">
                                <span className="text-xs sm:text-sm text-gray-600">{item.label}</span>
                                <span className="text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap text-right">{formatMoneyCompact(item.amount)}</span>
                              </div>
                            ))}
                          </React.Fragment>
                        ))}
                      </>
                    ) : (
                      <div className="px-9 py-2 text-xs sm:text-sm text-gray-400">No entries</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      ) : expenseTab !== 'salary' && (
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <h3 className="font-semibold text-gray-700 mb-3">{isNameBasedTab(expenseTab) ? 'Expense by Name' : 'Expense by Category'}</h3>
        <div className="flex flex-wrap gap-3">
          {Object.entries(filteredExpenses.reduce((acc, expense) => {
            const key = isNameBasedTab(expenseTab) ? getExpenseDisplayName(expense) : expense.category;
            acc[key] = (acc[key] || 0) + parseFloat(expense.amount || 0);
            return acc;
          }, {})).map(([category, amount]) => {
            const label = isNameBasedTab(expenseTab) ? category : getExpenseCategoryLabel(category);
            return (
              <div key={category} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                <span className="text-sm text-gray-600">{label}:</span>
                <span className="font-semibold">{formatMoneyCompact(amount)}</span>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (() => {
        const todayStr = formatDateValue(new Date());
        const firstDow = new Date(calendarMonth.year, calendarMonth.month, 1).getDay();
        const firstMon = firstDow === 0 ? 6 : firstDow - 1;
        const daysInMonth = new Date(calendarMonth.year, calendarMonth.month + 1, 0).getDate();
        const cells = Array(firstMon).fill(null);
        for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
        while (cells.length % 7 !== 0) cells.push(null);

        if (dateRangePreset === 'this_year') {
          const monthCards = Array.from({ length: 12 }, (_, monthIndex) => {
            const monthKey = `${calendarMonth.year}-${String(monthIndex + 1).padStart(2, '0')}`;
            const monthStart = `${monthKey}-01`;
            const monthEnd = formatDateValue(new Date(calendarMonth.year, monthIndex + 1, 0));
            const sourceRows = expenseTab === 'salary'
              ? salaryRows.filter((row) => row.date >= monthStart && row.date <= monthEnd)
              : displayExpenseRows.filter((row) => row.date >= monthStart && row.date <= monthEnd);
            return {
              monthKey,
              monthStart,
              monthEnd,
              total: sourceRows.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0),
              count: sourceRows.length,
            };
          });

          return (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCalendarMonth((prev) => ({ ...prev, year: prev.year - 1 }))}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="font-semibold text-gray-800">{calendarMonth.year}</h3>
                <button
                  onClick={() => setCalendarMonth((prev) => ({ ...prev, year: prev.year + 1 }))}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                {monthCards.map((monthCard) => (
                  <button
                    key={monthCard.monthKey}
                    type="button"
                    onClick={() => {
                      setCalendarMonth({ year: calendarMonth.year, month: Number(monthCard.monthKey.slice(5, 7)) - 1 });
                      setDateRange({ start: monthCard.monthStart, end: monthCard.monthEnd });
                      setDateRangePreset('this_month');
                    }}
                    className="text-left rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 hover:border-red-300 hover:shadow-sm transition"
                  >
                    <div className="text-sm font-semibold text-gray-800">{formatUiMonthYear(monthCard.monthStart)}</div>
                    <div className="mt-3 text-lg font-bold text-red-700">{formatMoneyCompact(monthCard.total)}</div>
                    <div className="mt-1 text-xs text-gray-500">{monthCard.count} entr{monthCard.count === 1 ? 'y' : 'ies'}</div>
                  </button>
                ))}
              </div>
            </div>
          );
        }

        return (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => changeCalendarMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h3 className="font-semibold text-gray-800">{formatUiMonthYear(`${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, '0')}-01`)}</h3>
              <button onClick={() => changeCalendarMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center mb-1">
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day => (
                <div key={day} className="text-xs text-gray-400 font-medium py-1">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, index) => {
                if (!day) return <div key={`empty-${index}`} />;
                const ds = `${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const holiday = getHolidayForDate(ds);
                const isToday = ds === todayStr;
                const isFuture = ds > todayStr;
                const isPast = ds < todayStr;
                const salaryInfo = salaryCalendarData[ds];
                const expenseInfo = expenseCalendarData[ds];
                const hasSalaryPaid = expenseTab === 'salary' && (salaryInfo?.paidTotal || 0) > 0;
                const hasBonus = expenseTab === 'salary' && (salaryInfo?.bonusTotal || 0) > 0;
                const hasGift = expenseTab === 'salary' && (salaryInfo?.giftTotal || 0) > 0;
                const hasAdvance = expenseTab === 'salary' && (salaryInfo?.advanceTotal || 0) > 0;
                const hasSalaryDue = expenseTab === 'salary' && (salaryInfo?.dueTotal || 0) >= 0 && (salaryInfo?.dueEntries?.length || 0) > 0;
                const hasDeduction = expenseTab === 'salary' && (salaryInfo?.deductionEntries?.length || 0) > 0;
                const hasExpense = expenseTab !== 'salary' && (expenseInfo?.count || 0) > 0;
                let cellClass = isPast ? 'bg-gray-300 border-gray-400' : 'bg-gray-50 border-gray-100';
                if (expenseTab === 'salary') {
                  if (hasSalaryPaid) cellClass = 'bg-green-50 border-green-300';
                  else if (hasBonus || hasGift || hasAdvance) cellClass = 'bg-green-50 border-green-300';
                  else if (hasDeduction) cellClass = 'bg-rose-50 border-rose-300';
                  else if (hasSalaryDue && isFuture) cellClass = 'bg-gray-300 border-gray-400 opacity-70';
                  else if (hasSalaryDue) cellClass = 'bg-orange-50 border-orange-300';
                } else if (hasExpense) {
                  cellClass = 'bg-red-50 border-red-300';
                }
                if (holiday && !hasExpense && !hasSalaryPaid && !hasSalaryDue && !hasAdvance && !hasBonus && !hasGift && !hasDeduction) {
                  cellClass = 'bg-amber-50 border-amber-300';
                }
                const clickable = (expenseTab === 'salary' && (hasSalaryPaid || hasSalaryDue || hasAdvance || hasBonus || hasGift || hasDeduction)) || hasExpense;
                return (
                  <div
                    key={ds}
                    onClick={() => {
                      if (!clickable) return;
                      if (expenseTab === 'salary' && isFuture && hasSalaryDue && !hasSalaryPaid && !hasAdvance) {
                        navigate(`/staff?tab=salary_schedule&date=${ds}&view=table`);
                        return;
                      }
                      setDateRange({ start: ds, end: ds });
                      setDateRangePreset('custom');
                      setViewMode('table');
                    }}
                    className={`rounded-lg border p-1.5 min-h-[74px] ${cellClass} ${holiday ? 'ring-1 ring-amber-300' : ''} ${clickable ? 'cursor-pointer hover:opacity-80' : ''} ${isToday ? 'ring-2 ring-red-400' : ''}`}
                  >
                    <div className="text-xs font-bold mb-0.5 text-gray-700">{day}</div>
                    {holiday && <div className="text-[9px] text-amber-700 font-semibold leading-tight truncate">{holiday.name}</div>}
                    {expenseTab === 'salary' ? (
                      <div className="space-y-0.5">
                        {hasSalaryPaid && renderSalaryCalendarEntries(salaryInfo.paidEntries, 'text-green-700', '✓')}
                        {hasBonus && renderSalaryCalendarEntries(salaryInfo.bonusEntries, 'text-green-700', '★')}
                        {hasGift && renderSalaryCalendarEntries(salaryInfo.giftEntries, 'text-green-700', '🎁')}
                        {hasAdvance && renderSalaryCalendarEntries(salaryInfo.advanceEntries, 'text-green-700', '+')}
                        {hasDeduction && renderSalaryCalendarEntries(salaryInfo.deductionEntries, 'text-rose-700', 'X')}
                        {hasSalaryDue && renderSalaryCalendarEntries(salaryInfo.dueEntries, isFuture ? 'text-gray-600' : 'text-orange-700', '?')}
                        {(() => {
                          const offs = Object.keys(offDayMap)
                            .filter(k => k.endsWith(`_${ds}`) && (!salaryStaffFilter || k.startsWith(`${salaryStaffFilter}_`)))
                            .map(k => offDayMap[k]);
                          const evs = Object.keys(lifecycleMap)
                            .filter(k => k.endsWith(`_${ds}`) && (!salaryStaffFilter || k.startsWith(`${salaryStaffFilter}_`)))
                            .flatMap(k => lifecycleMap[k]);
                          return (<>
                            {offs.map((r, i) => <LifecycleBadge key={`off-${i}`} type={r.violation_type} name={(r.staff_name || '').split(' ')[0]} filled={offDayFilled(r.violation_type, r.is_warning)} size={13} />)}
                            {evs.map((ev, i) => <LifecycleBadge key={`life-${i}`} type={ev.event_type} name={(ev.staff_name || '').split(' ')[0]} size={13} />)}
                          </>);
                        })()}
                      </div>
                    ) : hasExpense ? (
                      <div className="space-y-0.5">
                        <div className="text-[11px] text-red-700 font-bold leading-tight">{fmtK(expenseInfo.total)}</div>
                        <div className="text-[10px] text-blue-700 font-semibold leading-tight">{expenseInfo.count}</div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
              {expenseTab === 'salary' ? (
                <>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">✓</span> Paid</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">★</span> Bonus</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">🎁</span> Gift</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> <span className="text-green-700 font-semibold">+</span> Advance</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-50 border border-rose-300" /> <span className="text-rose-700 font-semibold">X</span> Deduction</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-50 border border-orange-300" /> <span className="text-orange-700 font-semibold">?</span> Due</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300" /> Holiday</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-300 border border-gray-400 opacity-70" /> <span className="text-gray-600 font-semibold">?</span> Future Date</span>
                  <EventLegend />
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-300" /> <span className="text-red-700 font-semibold">Amount (Rs)</span></span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300" /> Holiday</span>
                  <span className="inline-flex items-center gap-1"><span className="text-blue-700 font-semibold">N</span> Number of entries</span>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Salary Table */}
      {expenseTab === 'salary' && viewMode === 'table' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '780px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Due after event</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {salaryRows.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No salary records found</td></tr>
              ) : salaryRows.map(row => (
                <tr key={row.id} className={['paid', 'bonus', 'gift', 'advance'].includes(row.type) ? 'hover:bg-green-50' : row.type === 'deduction' ? 'hover:bg-rose-50' : 'hover:bg-orange-50'}>
                  <td className="px-4 py-3 text-gray-600">{formatDate(row.date)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{row.staffName}</div>
                    {row.department && <div className="text-xs text-gray-400">{row.department}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${['paid', 'bonus', 'gift', 'advance'].includes(row.type) ? 'bg-green-100 text-green-700' : row.type === 'deduction' ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700'}`}>
                      {row.type === 'paid' ? 'Paid Salary' : row.type === 'bonus' ? 'Bonus Paid' : row.type === 'gift' ? '🎁 Gift' : row.type === 'advance' ? 'Advance Paid' : row.type === 'deduction' ? 'Deduction' : 'Due Salary'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{row.status || '-'}</td>
                  <td className={`px-4 py-3 text-right font-bold ${['paid', 'bonus', 'gift', 'advance'].includes(row.type) ? 'text-green-700' : row.type === 'deduction' ? 'text-rose-700' : 'text-orange-700'}`}>{formatSalaryItemCurrency(row.amount)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${Number(row.remainingSigned ?? row.remainingDue ?? 0) < 0 ? 'text-emerald-700' : 'text-primary-700'}`}>
                    {formatSalaryEventDue(row, formatSalaryItemCurrency)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      {row.type !== 'due' && (
                        <button
                          onClick={() => openSalaryEditModal(row)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {row.type !== 'due' && (
                        <button
                          onClick={() => handleSalaryDelete(row.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expenseTab === 'salary' && viewMode === 'table' && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> Paid Salary</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> Bonus Paid</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> 🎁 Gift</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 border border-green-300" /> Advance Paid</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-50 border border-rose-300" /> Deduction</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-50 border border-orange-300" /> Due Salary</span>
        </div>
      )}

      {expenseTab !== 'salary' && viewMode === 'table' && (
      <>
      {/* Expenses Table - Desktop (DISABLED - unified into card layout below) */}
      <div className="hidden bg-white rounded-xl shadow-sm overflow-hidden overflow-x-auto">
        <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500 uppercase">{groupedExpenses.length} date{groupedExpenses.length !== 1 ? 's' : ''} • {displayExpenseRows.length} expense{displayExpenseRows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={expandAllDates} className="text-xs text-blue-600 hover:text-blue-800">Expand All</button>
            <button onClick={collapseAllDates} className="text-xs text-blue-600 hover:text-blue-800">Collapse All</button>
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expense #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-gray-500">Loading...</td>
              </tr>
            ) : groupedExpenses.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-gray-500">No expenses found</td>
              </tr>
            ) : (
              groupedExpenses.map(([date, expenses]) => {
                const isCollapsed = collapsedDates.has(date);
                const holiday = getHolidayForDate(date);
                return (
                  <React.Fragment key={date}>
                    <tr 
                      className={`${holiday ? 'bg-amber-50 hover:bg-amber-100' : 'bg-gray-100 hover:bg-gray-200'} cursor-pointer select-none transition`}
                      onClick={() => toggleDate(date)}
                    >
                      <td colSpan="8" className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {isCollapsed ? <ChevronRight size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
                          <span className="font-semibold text-gray-700">{formatExpenseDateHeader(date)}</span>
                          {holiday && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">{holiday.name}</span>}
                          <span className="text-xs text-gray-400">({expenses.length} {expenses.length === 1 ? 'entry' : 'entries'})</span>
                          <span className="ml-auto font-semibold text-red-600">{formatMoneyCompact(expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0))}</span>
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && expenses.map(expense => {
                      const isSalaryRow = expense.source_type === 'salary_payment';
                      const isPurchase = expense.category === 'raw_material';
                      const isInv = isPurchase && (expense.line_items || []).some(li => li.raw_material);
                      const categoryLabel = isSalaryRow
                        ? 'Salary'
                        : expense.category === 'raw_material'
                        ? 'Inventory'
                        : getExpenseCategoryLabel(expense);
                      const inventoryDisplay = expense.category === 'raw_material'
                        ? getInventoryExpenseDisplay(expense)
                        : { itemName: expense.description || '-', qtyText: '' };
                      const departmentDisplay = getExpenseDepartmentDisplay(expense);
                      const itemName = isSalaryRow ? (expense.staff_name || expense.description || '-') : inventoryDisplay.itemName;
                      const qtyText = isSalaryRow ? (expense.note || expense.status || '') : inventoryDisplay.qtyText;
                      return (
                        <tr key={expense.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">{formatDate(expense.date)}</td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-800">{expense.expense_number}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 bg-gray-100 rounded text-sm text-gray-700">
                              {categoryLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {departmentDisplay || <span className="text-gray-400 italic">{isSalaryRow ? 'Staff-wide' : 'Store-wide'}</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <div className="font-medium text-gray-800">{itemName}</div>
                            {qtyText && <div className="text-xs text-gray-500">{qtyText}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              expense.payment_method === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {expense.payment_method_display || expense.payment_method}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-red-600">
                            {formatMoneyDetailed(expense.amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-2">
                              {expense.invoice_image && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setViewingInvoice(expense); }}
                                  className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                                  title="View Invoice"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                              {!isSalaryRow && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openHistoryModal(expense); }}
                                  className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                                  title="Purchase history"
                                >
                                  <History className="w-4 h-4" />
                                </button>
                              )}
                              {isPurchase && isInv && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); goToInventoryItem(expense); }}
                                  className="p-1 text-teal-600 hover:bg-teal-50 rounded"
                                  title="Open inventory item"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                              {hasPermission('expenses', 'edit') && (
                                isSalaryRow ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openSalaryEditModal(expense.salary_row); }}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openEditModal(expense); }}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                )
                              )}
                              {hasPermission('expenses', 'delete') && (
                                isSalaryRow ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleSalaryDelete(expense.salary_row.id); }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(expense.id); }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Expenses Cards - Unified (desktop + mobile) */}
      <div className="space-y-3">
        <div className="px-4 py-2 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500 uppercase">{groupedExpenses.length} date{groupedExpenses.length !== 1 ? 's' : ''} • {displayExpenseRows.length} expense{displayExpenseRows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={expandAllDates} className="text-xs text-blue-600 hover:text-blue-800">Expand All</button>
            <button onClick={collapseAllDates} className="text-xs text-blue-600 hover:text-blue-800">Collapse All</button>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500 bg-white rounded-xl">Loading...</div>
        ) : groupedExpenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-white rounded-xl">No expenses found</div>
        ) : (
          groupedExpenses.map(([date, expenses]) => {
            const isCollapsed = collapsedDates.has(date);
            const holiday = getHolidayForDate(date);
            return (
              <div key={date} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div
                  className={`px-4 py-2 flex items-center justify-between cursor-pointer select-none transition ${holiday ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                  onClick={() => toggleDate(date)}
                >
                  <div className="flex items-center gap-2">
                    {isCollapsed ? <ChevronRight size={12} className="text-gray-500" /> : <ChevronDown size={12} className="text-gray-500" />}
                    <span className="text-xs font-medium text-gray-700">{formatExpenseDateHeader(date)}</span>
                    {holiday && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">{holiday.name}</span>}
                    <span className="text-[10px] text-gray-400">({expenses.length} {expenses.length === 1 ? 'entry' : 'entries'})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isCollapsed && hasPermission('expenses', 'delete') && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteAllForDate(date, expenses); }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete All for This Day"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                    <span className="text-xs font-medium text-red-600">{formatMoneyCompact(expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0))}</span>
                  </div>
                </div>
                {!isCollapsed && (
                  <div className="divide-y divide-gray-100 md:grid md:grid-cols-2 lg:grid-cols-3 md:divide-y-0 md:gap-px md:bg-gray-100">
                    {expenses.map(expense => {
                      const isSalaryRow = expense.source_type === 'salary_payment';
                      const invMatch = getInventoryMatch(expense);
                      const isInv = !!invMatch;
                      const isPurchase = expense.category === 'raw_material';
                      const inventoryDisplay = isPurchase
                        ? getInventoryExpenseDisplay(expense)
                        : { itemName: expense.description || '-', qtyText: '' };
                      // Prefer canonical inventory name when matched
                      const itemName = isSalaryRow
                        ? (expense.staff_name || expense.description || '-')
                        : inventoryDisplay.itemName;
                      const qtyText = inventoryDisplay.qtyText;
                      const categoryLabel = isPurchase
                        ? 'Inventory'
                        : getExpenseCategoryLabel(expense);
                      const categoryColor = isPurchase
                        ? (isInv ? 'text-gray-900 font-bold' : 'text-orange-600 font-semibold')
                        : 'text-gray-900 font-bold';
                      const displayQtyText = isSalaryRow ? (expense.note || expense.status || '') : qtyText;
                      const displayCategoryLabel = isSalaryRow ? 'Salary' : categoryLabel;
                      const displayCategoryColor = isSalaryRow ? 'text-emerald-700 font-semibold' : categoryColor;
                      const pmLabel = expense.payment_method_display || expense.payment_method;
                      const pmColor = getPaymentMethodColor(expense.payment_method);
                      if (isNameBasedTab(expenseTab)) {
                        return (
                          <div key={expense.id} className="p-4 bg-white">
                            <div className="flex items-start justify-between gap-2">
                              <div className="font-semibold text-gray-900 text-sm">{getExpenseDisplayName(expense)}</div>
                              <div className="text-sm font-bold text-red-600 whitespace-nowrap">{formatMoneyDetailed(expense.amount)}</div>
                            </div>
                            <div className="flex justify-end mt-0.5">
                              <span className="text-xs text-gray-400">{formatDate(expense.date)}</span>
                            </div>
                            <div className="flex justify-end mt-0.5">
                              <span className={`text-xs font-semibold ${isSalaryRow ? 'text-green-700' : pmColor}`}>{isSalaryRow ? (expense.salary_type === 'bonus' ? 'Bonus Paid' : expense.salary_type === 'gift' ? '🎁 Gift' : expense.salary_type === 'advance' ? 'Advance Paid' : 'Paid Salary') : getNameBasedPaymentLabel(expense)}</span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-[11px] font-mono text-gray-400">{expense.expense_number}</span>
                              <div className="flex gap-1">
                                {!isSalaryRow && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openHistoryModal(expense); }}
                                    className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                                    title="Payment history"
                                  >
                                    <History className="w-4 h-4" />
                                  </button>
                                )}
                                {hasPermission('expenses', 'edit') && (
                                  isSalaryRow ? (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openSalaryEditModal(expense.salary_row); }}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openEditModal(expense); }}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                  )
                                )}
                                {hasPermission('expenses', 'delete') && (
                                  isSalaryRow ? (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleSalaryDelete(expense.salary_row.id); }}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDelete(expense.id); }}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      const departmentDisplay = getExpenseDepartmentDisplay(expense);
                      return (
                        <div key={expense.id} className="p-4 bg-white">
                          {/* Row 1: Item Name  |  Rs Amount */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-semibold text-gray-900 text-sm">{itemName}</div>
                            <div className="text-sm font-bold text-red-600 whitespace-nowrap">{formatMoneyDetailed(expense.amount)}</div>
                          </div>
                          {/* Row 2: Qty unit (or Department for non-purchase)  |  Date */}
                          <div className="flex items-center justify-between mt-0.5">
                            <div className="text-xs">
                              {isSalaryRow ? (
                                displayQtyText ? <span className="text-gray-600">{displayQtyText}</span> : <span className="text-gray-400 italic">Salary payment</span>
                              ) : isPurchase ? (
                                <span className="text-gray-600">{displayQtyText || ''}</span>
                              ) : (
                                departmentDisplay
                                  ? <span className="text-gray-900 font-bold">{departmentDisplay}</span>
                                  : <span className="text-gray-400 italic">Store-wide</span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(expense.date)}</span>
                          </div>
                          {/* For purchases, show department on its own line (bold black) */}
                          {(isPurchase || isSalaryRow) && departmentDisplay && (
                            <div className="mt-0.5 text-xs text-gray-900 font-bold">{departmentDisplay}</div>
                          )}
                          {/* Spacer */}
                          <div className="mt-2" />
                          {/* Row 3: Category label  |  Payment Method */}
                          <div className="flex items-center justify-between">
                            <span className={`text-xs ${displayCategoryColor}`}>{displayCategoryLabel}</span>
                            <span className={`text-xs font-semibold ${pmColor}`}>{pmLabel}</span>
                          </div>
                          {/* Row 4: Expense Code  |  Edit/Delete */}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[11px] font-mono text-gray-400">{expense.expense_number}</span>
                            <div className="flex gap-1">
                              {expense.invoice_image && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setViewingInvoice(expense); }}
                                  className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                                  title="View Invoice"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                              {!isSalaryRow && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openHistoryModal(expense); }}
                                  className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                                  title="Purchase history"
                                >
                                  <History className="w-4 h-4" />
                                </button>
                              )}
                              {isPurchase && isInv && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); goToInventoryItem(expense); }}
                                  className="p-1 text-teal-600 hover:bg-teal-50 rounded"
                                  title="Open inventory item"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                              {hasPermission('expenses', 'edit') && (
                                isSalaryRow ? (
                                  <button onClick={() => openSalaryEditModal(expense.salary_row)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button onClick={() => openEditModal(expense)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                )
                              )}
                              {hasPermission('expenses', 'delete') && (
                                isSalaryRow ? (
                                  <button onClick={() => handleSalaryDelete(expense.salary_row.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Delete">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button onClick={() => handleDelete(expense.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Delete">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                {(editingExpense ? 'Edit ' : 'Add ') + (
                  formTab === 'purchases' ? 'Inventory Expense' :
                  formTab === 'maintenance' ? 'Maintenance Expense' :
                  formTab === 'bills' ? 'Bill Expense' :
                  formTab === 'salary' ? 'Salary Payment' :
                  editingExpense ? 'Expense' : 'New Expense')}
              </h2>
              <button onClick={closeExpenseModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {formTab === 'salary' ? (
                <>
                  {/* Salary Tab Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <UiDateField
                      value={formData.date}
                      onChange={(nextDate) => setFormData(prev => ({ ...prev, date: nextDate }))}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Staff Name *</label>
                    <select
                      value={formData.staff_id}
                      onChange={(e) => {
                        const selected = salaryStaffOptions.find(option => String(option.value) === String(e.target.value));
                        setFormData(prev => ({
                          ...prev,
                          staff_id: e.target.value,
                          description: selected?.label || '',
                        }));
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      required
                    >
                      <option value="">Select Staff</option>
                      {salaryStaffOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                      Payment Type *
                      <SalaryComputationInfo />
                      <span className="text-[11px] font-normal text-gray-400">How is salary computed?</span>
                    </label>
                    <select
                      value={formData.payment_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_type: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      required
                    >
                      <option value="salary">Salary</option>
                      <option value="advance">Advance</option>
                      <option value="bonus">Bonus</option>
                      <option value="gift">🎁 Gift</option>
                      <option value="deduction">Deduction</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.payment_type === 'advance' ? 'Advance reduces salary due' :
                       formData.payment_type === 'deduction' ? 'Deduction is a fine/penalty (shown in red)' :
                       formData.payment_type === 'bonus' ? 'Bonus payment' :
                       formData.payment_type === 'gift' ? 'Gift (kept separate from salary and bonus)' :
                       'Regular salary payment'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Rs.) *</label>
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      required
                    >
                      <option value="">Select Payment Method</option>
                      <option value="cash">Cash</option>
                      <option value="bank">Bank</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      rows="2"
                      placeholder="Enter notes..."
                    />
                  </div>
                </>
              ) : formTab === 'purchases' ? (
                <>
                  {/* Inventory Tab Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <UiDateField
                      value={formData.date}
                      onChange={(nextDate) => setFormData(prev => ({ ...prev, date: nextDate }))}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inventory Item *</label>
                    <select
                      value={formData.inventory_item}
                      onChange={(e) => {
                        const selected = inventoryItems.find(i => String(i.id) === e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          inventory_item: e.target.value,
                          description: selected?.name || '',
                          department: selected?.department || ''
                        }));
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      required
                    >
                      <option value="">Select Inventory Item</option>
                      {inventoryItems.map(item => (
                        <option key={item.id} value={item.id}>{item.name}{getInventoryUnitAbbr(item) ? ` (${getInventoryUnitAbbr(item)})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Qty *</label>
                      <input
                        type="number"
                        value={formData.qty}
                        onChange={(e) => setFormData(prev => ({ ...prev, qty: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        placeholder="0"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost (Rs.) *</label>
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      rows="2"
                      placeholder="Enter notes..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                      <input
                        type="text"
                        value={formData.vendor_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, vendor_name: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        placeholder="Vendor name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Contact</label>
                      <input
                        type="text"
                        value={formData.vendor_contact}
                        onChange={(e) => setFormData(prev => ({ ...prev, vendor_contact: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        placeholder="Contact number"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                    <div className="flex gap-2">
                      {PAYMENT_METHODS.map(pm => (
                        <button
                          key={pm.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, payment_method: pm.value }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-lg transition ${
                            formData.payment_method === pm.value
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {pm.icon}
                          {pm.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Maintenance/Bills/Other Tabs Form */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                    <UiDateField
                      value={formData.date}
                      onChange={(nextDate) => setFormData(prev => ({ ...prev, date: nextDate }))}
                    />
                  </div>
                  
                  {isNameBasedTab(formTab) ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                      <div className="flex gap-2">
                        <select
                          value={formData.expense_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, expense_name: e.target.value }))}
                          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                          required
                        >
                          <option value="">Select Category</option>
                          {(expenseNameOptions[formTab] || []).map(option => (
                            <option key={option.id} value={option.name}>{option.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={(event) => handleEditExpenseName(event)}
                          disabled={!formData.expense_name}
                          className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Edit Category"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => handleDeleteExpenseName(event)}
                          disabled={!formData.expense_name}
                          className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 hover:text-red-700 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Delete Category"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => handleAddExpenseName(event)}
                          className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 text-sm"
                          title="Add Category"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <div className="flex gap-2">
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        required
                      >
                        <option value="">Select Category</option>
                        {expenseCategories.map(category => (
                          <option key={category.value} value={category.value}>{category.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          const newCategory = prompt('Enter new category name:');
                          if (newCategory) {
                            toast.info('Category creation feature coming soon');
                          }
                        }}
                        className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 text-sm"
                        title="Add Category"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost (Rs.) *</label>
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                      rows="2"
                      placeholder="Enter notes..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                      <input
                        type="text"
                        value={formData.vendor_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, vendor_name: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        placeholder="Vendor name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Contact</label>
                      <input
                        type="text"
                        value={formData.vendor_contact}
                        onChange={(e) => setFormData(prev => ({ ...prev, vendor_contact: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        placeholder="Contact number"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                    <div className="flex gap-2">
                      {PAYMENT_METHODS.map(pm => (
                        <button
                          key={pm.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, payment_method: pm.value }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-lg transition ${
                            formData.payment_method === pm.value
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {pm.icon}
                          {pm.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {!editingExpense && (
                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={handleApplyExpensePending}
                    className="px-4 py-2 border border-red-200 text-red-700 rounded-lg hover:bg-red-50 flex items-center justify-center gap-2"
                  >
                    Apply
                  </button>
                </div>
              )}

              {!editingExpense && pendingExpenseEntries.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pending ({pendingExpenseEntries.length} entries)</p>
                  <div className="border rounded-lg divide-y overflow-hidden">
                    {pendingExpenseEntries.map((entry, index) => (
                      <div key={entry._key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-gray-50">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">{entry.tab === 'purchases' ? 'Inventory' : entry.tab === 'salary' ? 'Salary' : entry.tab}</span>
                            <span className="font-medium text-gray-800 truncate">{entry.label}</span>
                            <span className="text-gray-500">{formatUiDate(entry.date)}</span>
                            <span className="text-gray-700">{formatUiCurrencyDetailed(entry.amount)}</span>
                          </div>
                        </div>
                        <button type="button" onClick={() => setPendingExpenseEntries(prev => prev.filter((_, i) => i !== index))} className="p-1 text-gray-400 hover:text-red-600 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeExpenseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  {editingExpense ? 'Save Changes' : `Save All (${pendingExpenseEntries.length || 1})`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Smart Paste Modal */}
      {showSmartPaste && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Smart Paste - {expenseTab === 'purchases' ? 'Inventory' : expenseTab === 'bills' ? 'Bills' : expenseTab === 'maintenance' ? 'Maintenance' : expenseTab === 'salary' ? 'Salary' : 'Expenses'}
              </h2>
              <button onClick={() => setShowSmartPaste(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              {smartPasteStep === 'input' ? (
                <>
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Instructions:</strong> Paste your data in tab-separated or CSV format. Each line should contain: Item Name, Quantity, Unit Price
                    </p>
                  </div>
                  <textarea
                    value={smartPasteText}
                    onChange={(e) => setSmartPasteText(e.target.value)}
                    placeholder="Paste your data here...&#10;Example:&#10;Item1	10	500&#10;Item2	5	1000"
                    className="w-full h-64 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowSmartPaste(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={processSmartPasteText}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Process
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {formTab === 'purchases' && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Inventory Mode:</strong> Each item must match an inventory item. Items without matches will be skipped.
                      </p>
                    </div>
                  )}
                  
                  {smartPasteData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No data to display
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[240px]">Item Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                            {expenseTab === 'purchases' && (
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                            )}
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total (Rs)</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor Contact</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {smartPasteData.map((item, index) => (
                            <tr key={item.id} className={!item.matchedItem && expenseTab === 'purchases' ? 'bg-red-50' : ''}>
                              <td className="px-3 py-2 min-w-[240px]">
                                {expenseTab === 'purchases' ? (
                                  <select
                                    value={item.matchedItem?.id || ''}
                                    onChange={(e) => {
                                      const selected = inventoryItems.find(inv => String(inv.id) === e.target.value) || null;
                                      const newData = [...smartPasteData];
                                      newData[index] = {
                                        ...newData[index],
                                        matchedItem: selected,
                                        itemName: selected?.name || newData[index].itemName,
                                        units: getInventoryUnitAbbr(selected) || newData[index].units || '',
                                      };
                                      setSmartPasteData(newData);
                                    }}
                                    className="w-full px-2 py-1 border rounded text-sm"
                                  >
                                    <option value="">-- Select inventory item ({item.itemName}) --</option>
                                    {inventoryItems.map(inv => (
                                      <option key={inv.id} value={inv.id}>{inv.name}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    value={item.itemName}
                                    onChange={(e) => {
                                      const newData = [...smartPasteData];
                                      newData[index].itemName = e.target.value;
                                      setSmartPasteData(newData);
                                    }}
                                    className="w-full px-2 py-1 border rounded"
                                  />
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={item.qty}
                                  onChange={(e) => {
                                    const newData = [...smartPasteData];
                                    newData[index].qty = parseFloat(e.target.value) || 0;
                                    setSmartPasteData(newData);
                                  }}
                                  className="w-20 px-2 py-1 border rounded"
                                  min="0"
                                />
                              </td>
                              {expenseTab === 'purchases' && (
                                <td className="px-3 py-2">
                                  {item.matchedItem ? getInventoryUnitAbbr(item.matchedItem) || item.units || '-' : item.units || '-'}
                                </td>
                              )}
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={item.totalCost || item.totalAmount}
                                  onChange={(e) => {
                                    const newData = [...smartPasteData];
                                    newData[index].totalCost = parseFloat(e.target.value) || 0;
                                    setSmartPasteData(newData);
                                  }}
                                  className="w-24 px-2 py-1 border rounded"
                                  min="0"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={item.notes || ''}
                                  onChange={(e) => {
                                    const newData = [...smartPasteData];
                                    newData[index].notes = e.target.value;
                                    setSmartPasteData(newData);
                                  }}
                                  className="w-32 px-2 py-1 border rounded"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={item.vendorName || ''}
                                  onChange={(e) => {
                                    const newData = [...smartPasteData];
                                    newData[index].vendorName = e.target.value;
                                    setSmartPasteData(newData);
                                  }}
                                  className="w-32 px-2 py-1 border rounded"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={item.vendorContact || ''}
                                  onChange={(e) => {
                                    const newData = [...smartPasteData];
                                    newData[index].vendorContact = e.target.value;
                                    setSmartPasteData(newData);
                                  }}
                                  className="w-32 px-2 py-1 border rounded"
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => {
                                      // Populate form with item data and open modal
                                      setFormData(prev => ({
                                        ...prev,
                                        date: formData.date,
                                        expense_name: isNameBasedTab(expenseTab) ? item.itemName : prev.expense_name,
                                        description: item.itemName,
                                        amount: item.totalCost || item.totalAmount,
                                        vendor_name: item.vendorName || '',
                                        vendor_contact: item.vendorContact || '',
                                        notes: item.notes || '',
                                        inventory_item: item.matchedItem?.id || '',
                                        qty: item.qty || '',
                                      }));
                                      setShowSmartPaste(false);
                                      setShowModal(true);
                                    }}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const newData = smartPasteData.filter((_, i) => i !== index);
                                      setSmartPasteData(newData);
                                    }}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-4 border-t mt-4">
                    <button
                      onClick={() => setSmartPasteStep('input')}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setShowSmartPaste(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveSmartPasteExpenses}
                      disabled={smartPasteData.length === 0 || smartPasteSaving}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {smartPasteSaving ? 'Saving...' : `Save All (${smartPasteData.length})`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Image Lightbox */}
      {historyModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={() => setHistoryModal(null)}>
          <div className="relative max-w-2xl w-full max-h-[85vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-amber-600" />
                <div>
                  <h3 className="font-semibold text-gray-800">{historyModal.title}</h3>
                  <p className="text-xs text-gray-500">
                    {historyModal.data?.mode === 'inventory' ? 'Purchase history (all time)' : 'Payment history (all time)'}
                  </p>
                </div>
              </div>
              <button onClick={() => setHistoryModal(null)} className="p-2 hover:bg-gray-200 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-auto">
              {historyModal.loading ? (
                <div className="py-12 text-center text-gray-400">
                  <span className="inline-block w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  <p className="mt-2 text-sm">Loading history…</p>
                </div>
              ) : !historyModal.data?.rows?.length ? (
                <p className="py-12 text-center text-sm text-gray-400">No history found.</p>
              ) : historyModal.data.mode === 'inventory' ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2">Date</th>
                      <th className="py-2 text-right">Qty</th>
                      <th className="py-2 text-right">Cost / Unit</th>
                      <th className="py-2 text-right">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historyModal.data.rows.map((row, i) => (
                      <tr key={i} className={row.is_current ? 'bg-amber-50' : ''}>
                        <td className="py-2 text-gray-600">{formatDate(row.date)}{row.is_current && <span className="ml-1 text-[10px] text-amber-600 font-semibold">(this)</span>}</td>
                        <td className="py-2 text-right tabular-nums">{row.quantity} {row.unit}</td>
                        <td className="py-2 text-right tabular-nums text-gray-600">{formatMoneyDetailed(row.unit_cost)}/{row.unit || 'unit'}</td>
                        <td className="py-2 text-right font-semibold text-red-600 tabular-nums">{formatMoneyDetailed(row.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 font-semibold">
                      <td className="py-2">Total ({historyModal.data.rows.length})</td>
                      <td className="py-2 text-right tabular-nums">{historyModal.data.totals.quantity} {historyModal.data.unit}</td>
                      <td className="py-2 text-right text-gray-500 tabular-nums">avg {formatMoneyDetailed(historyModal.data.totals.avg_unit_cost)}/{historyModal.data.unit || 'unit'}</td>
                      <td className="py-2 text-right text-red-700 tabular-nums">{formatMoneyDetailed(historyModal.data.totals.total_cost)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2">Date</th>
                      <th className="py-2">Payment</th>
                      <th className="py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historyModal.data.rows.map((row, i) => (
                      <tr key={i} className={row.is_current ? 'bg-amber-50' : ''}>
                        <td className="py-2 text-gray-600">{formatDate(row.date)}{row.is_current && <span className="ml-1 text-[10px] text-amber-600 font-semibold">(this)</span>}</td>
                        <td className="py-2 text-gray-600 capitalize">{row.payment_method}</td>
                        <td className="py-2 text-right font-semibold text-red-600 tabular-nums">{formatMoneyDetailed(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 font-semibold">
                      <td className="py-2" colSpan={2}>Total ({historyModal.data.totals.count})</td>
                      <td className="py-2 text-right text-red-700 tabular-nums">{formatMoneyDetailed(historyModal.data.totals.total_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {viewingInvoice && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" onClick={() => setViewingInvoice(null)}>
          <div className="relative max-w-3xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-800">Invoice / Receipt</h3>
                <p className="text-sm text-gray-500">{viewingInvoice.expense_number} | {formatDate(viewingInvoice.date)} | {formatMoneyDetailed(viewingInvoice.amount)}</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={viewingInvoice.invoice_image} target="_blank" rel="noopener noreferrer" className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium flex items-center gap-1">
                  Open Full
                </a>
                <button onClick={() => setViewingInvoice(null)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-100 overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              <img src={viewingInvoice.invoice_image} alt="Invoice" className="max-w-full max-h-[75vh] object-contain rounded-lg shadow" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
